import style from "@/styles/pages/Assets/Assets.module.css";
import { FormProvider, set, useForm } from "react-hook-form";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "@/store/dtm";
import CurrencyInput from "react-currency-input-field";
import DebouncedCurrencyInput from "@/components/shared/DebouncedCurrencyInput";
import DebouncedTextInput from "@/components/shared/DebouncedTextInput";
import { setApplicationProgress } from "@/slices/applicationSlice";
import SlideTransition from "@/styles/animation/SlideTransition";
import { createApiClient } from "@/lib/Requests";
import { AddressDto, ApplicationDto, AssetDto, DealAssetsDto, DealExpensesDto, FinancialAssetDto, LifeInsuranceDto, Occupation, PropertyExpenseDto, RealEstateDto, VehicleDto, VehicleModel } from "@/lib/DTMWebAPIClient.generated";
import { selectPropertyInfo } from "@/slices/propertyInfoSlice";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { addAsset, assetInfo, AssetOwnership, clearAssetState, removeAsset, setAssetInfo, toggleAssetDisplay, VehicleOption } from "@/slices/assetSlice";
import { selectCoApplicant } from "@/slices/coApplicantSlice";
import SearchableDropdown from "@/components/shared/SearchableDropdown";
import ManualAddress from "@/components/Address/ManualAddress";
import { streetDirections } from "@/ts/streetDirections";
import { streetTypes } from "@/ts/streetTypes";
import { vehicleMakes, motorcycleMakes, boatMakes, atvMakes } from "@/ts/vehicleMakes";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { setCachedCurrentEmployment } from "@/slices/sessionDataSlice";
import { selectCurrentEmployment } from "@/slices/currentEmploymentSlice";
import { setClickInsightEvents, setClickInsightFormErrors, setClickInsightManualFormErrors } from "@/lib/util";
import { expenseInfo, addExpense, setExpenseInfo, clearExpenseState, removeExpense } from "@/slices/expenseSlice";
import { setResidentStatus } from "@/slices/applicantSlice";

export default function Assets() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();

    const applicant = useSelector((state: RootState) => state.applicant.applicant);
    const currentEmployment = useSelector(selectCurrentEmployment).employments;
    const coapplicants = useSelector(selectCoApplicant).coapplicants;
    const sessionData = useSelector((state: RootState) => state.sessionData.currentSession);
    const propertyInfo = useSelector(selectPropertyInfo).propertyInfo;
    const [displayError, setDisplayError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [addressSelectionError, setAddressSelectionError] = useState(false);
    const [assetErrors, setAssetErrors] = useState<Record<string, string>[]>([]);
    const [assetAddrErrors, setAssetAddrErrors] = useState<string[][]>([]);
    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const homeaddress = useSelector(
        (state: RootState) => state.homeAddress.address
    );
    const credential = useSelector(
        (state: RootState) => state.credential
    );
    const assets = useSelector((state: RootState) => state.assets.assets);
    const expenses = useSelector((state: RootState) => state.expenses.expenses);
    const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

    const methods = useForm({
        defaultValues: {},
    });
    const [applicantList, setApplicantList] = useState<AssetOwnership[]>([]);
    const [vehicleModels, setVehicleModels] = useState<VehicleOption[]>([]);
    const [modelYears, setModelYears] = useState<string[]>([]);
    const [fetchingModels, setFetchingModels] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [fetchingCachedData, setFetchingCachedData] = useState(false);
    const [addingOwnRealEstate, setAddingOwnRealEstate] = useState(false);

    const options = {
        key: "XD95-YY77-BP99-EM55",
    };

    useEffect(() => {
        document.title = "Enter Down Payment Source";

        setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);
        dispatch(setCachedCurrentEmployment(currentEmployment));

        client.apiApplicationsFetch().then((result) => {
            result.application = application;
            result.propertyInfo = propertyInfo;
            result.currentEmployment = currentEmployment;
            client.apiApplicationsSave(new ApplicationDto({
                applicationData: JSON.stringify(result)
            })).catch((error) => {
                console.log("Error saving application info");
            });
        }).catch((error) => {
            console.log("Error fetching application info", error);
        });

        let applicantList: AssetOwnership[] = [];
        applicantList.push({
            applicantId: applicant.id,
            fullname: `${applicant.firstName} ${applicant.lastName}`,
        });

        const currentYear = new Date().getFullYear();
        const years: string[] = [];
        for (let year = 1923; year <= currentYear; year++) {
            years.push(year.toString());
        }
        setModelYears(years.reverse());
        setApplicantList(applicantList);

        if (state != null) {
            setEditMode(true);
        }
        let addingOwn = false;
        if (state != null && (state as any).ownCurrentAddress === true) {
            setAddingOwnRealEstate(true);
            addingOwn = true;
        }

        //Fetch Cached Asset Data (if Exists)
        if (sessionData.cachedAssets && sessionData.cachedAssets.length > 0 && !addingOwn) {
            dispatch(clearAssetState());
            sessionData.cachedAssets.forEach((asset) => {
                dispatch(addAsset(asset));
            });
        }
    }, []);

    useEffect(() => {
        let astErrors: string[][] = [];
        for (let i = 0; i < assetErrors.length; i++) {
            const errorObj = assetErrors[i];
            let currentAsset = [];
            for (const key in errorObj) {
                currentAsset.push(key);
            }
            astErrors.push(currentAsset);
        }
        setAssetAddrErrors(astErrors);
    }, [assetErrors]);

    const onError = (errors: object) => {
        setClickInsightFormErrors(errors);
    }

    const onSubmit = async (data: any) => {
        let emptyFieldExists = false;

        let assetErrors: Record<string, string>[] = [];
        let assetErrorIndices: number[] = [];

        assets.forEach((a, index) => {
            let errors: Record<string, string> = {};
            if (a.assetCategory.trim() == "") {
                emptyFieldExists = true;
                errors["assetCategory"] = "Select an asset type.";
            }
            if ((a.valueText.trim() == "" || a.value <= 0) && a.origin != "internal") {
                emptyFieldExists = true;
                errors["assetValue"] = "Enter the asset value.";
            }
            if (a.applicants.length == 0) {
                emptyFieldExists = true;
                errors["ownership"] = "Select an asset owner.";
            }
            if (a.downPaymentValue > a.value) {
                emptyFieldExists = true;
                errors["assetDownPayment"] = "Invalid amount. Cannot exceed value of asset.";
            }

            if (a.assetCategory === "nonregistered" || a.assetCategory === "registered") {
                if (a.assetType.trim() == "") {
                    emptyFieldExists = true;
                    errors["assetType"] = "Select an asset type.";
                }
            }

            if (a.assetCategory === "vehicle") {
                if (a.assetType.trim() == "") {
                    emptyFieldExists = true;
                    errors["assetType"] = "Select a vehicle type.";
                }
            }

            if (a.assetCategory === "nonregistered" || a.assetCategory === "registered" || a.assetCategory === "vehicle"
                || a.assetCategory === "realestate" || a.assetCategory === "lifeinsurance" || a.assetCategory === "other") {
                if (a.downPaymentValueText.trim() == "" || a.downPaymentValue < 0) {
                    emptyFieldExists = true;
                    errors["assetDownPayment"] = "Enter the amount you will use for your down payment.";
                }
            }

            if (a.assetCategory === "nonregistered" || a.assetCategory === "registered") {

                if (a.assetCategory === "nonregistered") {
                    if (a.details?.nonRegistered?.financialInstitution == undefined || a.details?.nonRegistered?.financialInstitution.trim() == "") {
                        emptyFieldExists = true;
                        errors["fiName"] = "Select a financial institution.";
                    }
                    if (a.details?.nonRegistered?.financialInstitution != undefined && a.details?.nonRegistered?.financialInstitution.trim() == "other" &&
                        (a.details?.nonRegistered?.otherFinancialInstitution == undefined || a.details?.nonRegistered?.otherFinancialInstitution.trim() == "")) {
                        emptyFieldExists = true;
                        errors["fiOtherName"] = "Enter the financial institution's name.";
                    }
                } else {
                    if (a.details?.registered?.financialInstitution == undefined || a.details?.registered?.financialInstitution.trim() == "") {
                        emptyFieldExists = true;
                        errors["fiName"] = "Select a financial institution.";
                    }
                    if (a.details?.registered?.financialInstitution != undefined && a.details?.registered?.financialInstitution.trim() == "other" &&
                        (a.details?.registered?.otherFinancialInstitution == undefined || a.details?.registered?.otherFinancialInstitution.trim() == "")) {
                        emptyFieldExists = true;
                        errors["fiOtherName"] = "Enter the financial institution's name.";
                    }
                }
            }

            if (a.assetCategory === "vehicle") {
                if (a.assetType.trim() == "") {
                    emptyFieldExists = true;
                    errors["assetType"] = "Select a vehicle type.";
                }
                if ((a.assetType === "car" || a.assetType === "truck") && (a.details?.vehicle?.ownership == undefined || a.details?.vehicle?.ownership.trim() == "")) {
                    emptyFieldExists = true;
                    errors["ownershipType"] = "Select an ownership type.";
                }
                if (a.details?.vehicle?.year == undefined || a.details?.vehicle?.year.trim() == "") {
                    emptyFieldExists = true;
                    errors["vehicleYear"] = "Enter a year.";
                }
                if (a.details?.vehicle?.make == undefined || a.details?.vehicle?.make.trim() == "") {
                    emptyFieldExists = true;
                    errors["vehicleMake"] = "Enter a make.";
                }
                if ((a.assetType === "car" || a.assetType === "truck" || a.assetType === "motorcycle")
                    && (a.details?.vehicle?.model == undefined || a.details?.vehicle?.model.trim() == "")) {
                    emptyFieldExists = true;
                    errors["vehicleModel"] = "Enter a model.";
                }
            }

            if (a.assetCategory === "realestate") {
                let realEstateAddress = a.details?.realEstate?.address;
                if (!a.details?.realEstate?.manualAddressEntered && (realEstateAddress?.streetNumber.trim() == "" || realEstateAddress?.streetName.trim() == "" || realEstateAddress?.city.trim() == "" || realEstateAddress?.country.trim() == "")) {
                    emptyFieldExists = true;
                    errors["autoAddress"] = "Select an address";
                }
                if (realEstateAddress?.streetNumber.trim() == "") {
                    emptyFieldExists = true;
                    errors["streetNumber"] = "Enter your street number.";
                }
                if (realEstateAddress?.streetType.trim() == "") {
                    emptyFieldExists = true;
                    errors["streetType"] = "Select a street type.";
                }
                if (realEstateAddress?.streetName.trim() == "") {
                    emptyFieldExists = true;
                    errors["streetName"] = "Enter your street name.";
                }
                if (realEstateAddress?.city.trim() == "") {
                    emptyFieldExists = true;
                    errors["city"] = "Enter your city name.";
                }
                if (realEstateAddress?.province.trim() == "" && (realEstateAddress?.country.trim() == "Canada" || realEstateAddress?.country.trim() == "United States")) {
                    emptyFieldExists = true;
                    errors["province"] = "Enter your province name.";
                }
                if (realEstateAddress?.postalCode.trim() == "" && (realEstateAddress?.country.trim() == "Canada" || realEstateAddress?.country.trim() == "United States")) {
                    emptyFieldExists = true;
                    errors["postalCode"] = "Enter your postal code.";
                }
                if (realEstateAddress?.country.trim() == "") {
                    emptyFieldExists = true;
                    errors["country"] = "Enter your country.";
                }
                if (a.details?.realEstate?.occupancyType == undefined || a.details?.realEstate?.occupancyType.trim() == "") {
                    emptyFieldExists = true;
                    errors["useOfProperty"] = "Select a use of property.";
                }
                if (a.details?.realEstate?.futureUsage == undefined || a.details?.realEstate?.futureUsage.trim() == "") {
                    emptyFieldExists = true;
                    errors["futureUsage"] = "Select a future usage.";
                }
                if (a.details?.realEstate?.occupancyType !== "vacantland" && (a.details?.realEstate?.propertyType == undefined || a.details?.realEstate?.propertyType.trim() == "")) {
                    emptyFieldExists = true;
                    errors["propertyType"] = "Select a property type.";
                }
                if (a.details?.realEstate?.occupancyType == "rental") {
                    if (a.details?.realEstate?.rentalIncome == undefined || a.details?.realEstate?.rentalIncome == 0) {
                        emptyFieldExists = true;
                        errors["rentalIncome"] = "Enter your rental income.";
                    }
                }
                if (a.details?.realEstate?.futureUsage == "selling") {
                    if (a.details?.realEstate?.hasPropertyBeenSold == undefined || a.details?.realEstate?.hasPropertyBeenSold.trim() == "") {
                        emptyFieldExists = true;
                        errors["propertySold"] = "Select an option.";
                    }
                    if (a.details?.realEstate?.hasPropertyBeenSold == "yes") {
                        if (a.details?.realEstate?.soldDate == undefined || a.details?.realEstate?.soldDate.trim() == "") {
                            emptyFieldExists = true;
                            errors["soldDate"] = "Enter a closing date.";
                        }
                    }
                }
                if (a.details.realEstate?.occupancyType === "primary") {
                    if (a.details?.realEstate?.rentalComponent == undefined || a.details?.realEstate?.rentalComponent.trim() == "") {
                        emptyFieldExists = true;
                        errors["rentalComponent"] = "Select an option.";
                    }
                    if (a.details?.realEstate?.rentalComponent != undefined && a.details?.realEstate?.rentalComponent.trim() === "yes") {
                        if (a.details?.realEstate?.rentalIncome == undefined || a.details?.realEstate?.rentalIncome == 0) {
                            emptyFieldExists = true;
                            errors["rentalIncome"] = "Enter your rental income.";
                        }
                    }
                }

                if (a.details.realEstate?.occupancyType === "rental") {
                    if (a.details?.realEstate?.ownershipPercentage == undefined || a.details?.realEstate?.ownershipPercentage.trim() == "") {
                        emptyFieldExists = true;
                        errors["ownershipPercentage"] = "Enter the ownership of this property in percentage value.";
                    }

                    if (
                        a.details?.realEstate?.ownershipPercentage != undefined &&
                        a.details?.realEstate?.ownershipPercentage.trim() != ""
                    ) {
                        const ownershipVal = a.details?.realEstate?.ownershipPercentage.trim();
                        const ownershipNum = Number(ownershipVal);
                        if (
                            !/^\d+$/.test(ownershipVal) ||
                            ownershipNum <= 0 ||
                            ownershipNum > 100
                        ) {
                            emptyFieldExists = true;
                            errors["ownershipPercentage"] = "Please enter a whole number between 1 and 100.";
                        }
                    }
                }
                if (a.details.realEstate?.propertyTaxText == undefined || a.details.realEstate?.propertyTaxText.trim() == "") {
                    emptyFieldExists = true;
                    errors["propertyTax"] = "Enter the property tax amount.";
                } else {
                    if (a.details.realEstate?.propertyTax != undefined && a.details.realEstate?.propertyTax == 0) {
                        emptyFieldExists = true;
                        errors["propertyTax"] = "Property tax cannot be zero.";
                    }
                }

                if (a.details.realEstate?.heatingCostText == undefined || a.details.realEstate?.heatingCostText.trim() == "") {
                    emptyFieldExists = true;
                    errors["heatingCost"] = "Enter the heating/utility fees.";
                } else {
                    if (a.details.realEstate?.heatingCost != undefined && a.details.realEstate?.heatingCost == 0) {
                        emptyFieldExists = true;
                        errors["heatingCost"] = "Heating/utility fees cannot be zero.";
                    }
                }

                if (a.details.realEstate?.propertyType == "condo" &&
                    (a.details.realEstate?.maintenanceFeeText == undefined || a.details.realEstate?.maintenanceFeeText.trim() == "")) {
                    emptyFieldExists = true;
                    errors["maintenanceFee"] = "Enter the condo/maintenance fee.";
                }
            }

            if (a.assetCategory === "lifeinsurance") {
                if (a.details?.lifeInsurance?.cashSurrenderValueText == undefined || a.details?.lifeInsurance?.cashSurrenderValueText.trim() == "") {
                    emptyFieldExists = true;
                    errors["cashSurrenderValue"] = "Enter the cash surrender value.";
                }
                if ((a.valueText.trim() == "" || a.value <= 0) && a.origin != "internal") {
                    emptyFieldExists = true;
                    errors["assetValue"] = "Enter the policy coverage amount.";
                }
                if (a.description.trim() == "") {
                    emptyFieldExists = true;
                    errors["assetDescription"] = "Enter a description.";
                }
            }

            if (a.assetCategory === "other") {
                if (a.description.trim() == "") {
                    emptyFieldExists = true;
                    errors["assetDescription"] = "Enter a description of the asset.";
                }
            }

            if (a.assetCategory === "builderdeposit" || a.assetCategory === "depositonpurchase") {
                if ((a.valueText.trim() == "" || a.value <= 0) && a.origin != "internal") {
                    emptyFieldExists = true;
                    errors["assetValue"] = "Enter the value of the deposit.";
                }
            }

            if (a.assetCategory === "gift") {
                if ((a.valueText.trim() == "" || a.value <= 0) && a.origin != "internal") {
                    emptyFieldExists = true;
                    errors["assetValue"] = "Enter the value of the gift.";
                }
            }

            if (Object.keys(errors).length > 0) {
                let newAssetInfo: assetInfo = { ...a }
                newAssetInfo.visible = true;
                dispatch(setAssetInfo(newAssetInfo));
            }

            assetErrors.push(errors);
        });
        setAssetErrors(assetErrors);
        if (emptyFieldExists) {
            setErrorMessage("One or more fields is missing in your asset(s).");
            setDisplayError(true);
            window.scroll(0, 0);

            let errorMessages: string[] = [];
            // Traverse through the assetErrors array and log each error object
            assetErrors.forEach((errorObj, idx) => {
                Object.entries(errorObj).forEach(([key, value]) => {
                    errorMessages.push(`Asset ${idx + 1} - ${key}: ${value}`);
                });
            });
            setClickInsightManualFormErrors(errorMessages);
            return;
        }

        setIsSaving(true);
        let nonRegistered: FinancialAssetDto[] = [];
        let registered: FinancialAssetDto[] = [];
        let realEstate: RealEstateDto[] = [];
        let vehicle: VehicleDto[] = [];
        let lifeInsurance: LifeInsuranceDto[] = [];
        let other: AssetDto[] = [];
        let downPayment: AssetDto[] = [];

        assets.forEach((asset: assetInfo) => {
            switch (asset.assetCategory) {
                case "nonregistered":
                    nonRegistered.push(new FinancialAssetDto({
                        fiName: asset.details.nonRegistered?.financialInstitution,
                        fiNameOther: asset.details.nonRegistered?.otherFinancialInstitution,
                        id: asset.id,
                        assetCategoryType_Name: asset.assetCategory,
                        assetType_Name: asset.assetType == "term_gic" ? "termgic" : asset.assetType,
                        origin_Name: "manual",
                        value: asset.value,
                        description: asset.description,
                        downPaymentValue: asset.downPaymentValue,
                        applicants: asset.applicants,
                    }));
                    break;
                case "registered":
                    registered.push(new FinancialAssetDto({
                        fiName: asset.details.registered?.financialInstitution,
                        fiNameOther: asset.details.registered?.otherFinancialInstitution,
                        id: asset.id,
                        assetCategoryType_Name: asset.assetCategory,
                        assetType_Name: asset.assetType,
                        origin_Name: "manual",
                        value: asset.value,
                        description: asset.description,
                        downPaymentValue: asset.downPaymentValue,
                        applicants: asset.applicants,
                    }));
                    break;
                case "vehicle":
                    vehicle.push(new VehicleDto({
                        vehicleOwnershipType_Name: asset.details.vehicle?.ownership,
                        year: asset.details.vehicle?.year ? +asset.details.vehicle.year : 0,
                        make: asset.details.vehicle?.make,
                        model: asset.details.vehicle?.model,
                        id: asset.id,
                        assetCategoryType_Name: asset.assetCategory,
                        assetType_Name: asset.assetType,
                        origin_Name: "manual",
                        value: asset.value,
                        description: asset.description,
                        downPaymentValue: asset.downPaymentValue,
                        applicants: asset.applicants,
                    }));
                    break;
                case "realestate":
                    let originalDate = undefined;
                    if (asset?.details?.realEstate?.originalPurchaseDate && asset?.details?.realEstate?.originalPurchaseDate.trim() != "") {
                        let dateComponents = asset.details.realEstate.originalPurchaseDate.split("/");
                        originalDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
                    }
                    let soldDate = undefined;
                    if (asset?.details?.realEstate?.soldDate && asset?.details?.realEstate?.soldDate.trim() != "") {
                        let dateComponents = asset.details.realEstate.soldDate.split("/");
                        soldDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
                    }
                    realEstate.push(new RealEstateDto({
                        occupancyType_Name: asset.details.realEstate?.occupancyType,
                        propertyType_Name: asset.details.realEstate?.propertyType,
                        rentalComponent: asset.details.realEstate?.rentalComponent === "yes",
                        numberOfProperties: asset.details.realEstate?.numOfProperties ? +asset.details.realEstate?.numOfProperties : 0,
                        assetAddress_StreetNumber: asset.details.realEstate?.address?.streetNumber,
                        assetAddress_StreetName: asset.details.realEstate?.address?.streetName,
                        assetAddress_StreetType: asset.details.realEstate?.address?.streetType,
                        assetAddress_StreetDirection: asset.details.realEstate?.address?.streetDirection,
                        assetAddress_UnitType: asset.details.realEstate?.address?.unitType,
                        assetAddress_UnitNumber: asset.details.realEstate?.address?.unitNumber,
                        assetAddress_City: asset.details.realEstate?.address?.city,
                        assetAddress_PostalCode: asset.details.realEstate?.address?.postalCode,
                        assetAddress_Region: asset.details.realEstate?.address?.province,
                        assetAddress_Country: asset.details.realEstate?.address?.country,
                        origPurchasePrice: asset.details.realEstate?.originalPurchasePrice,
                        origPurchaseDate: originalDate,
                        lot: asset.details.realEstate?.lot,
                        block: asset.details.realEstate?.block,
                        plan: asset.details.realEstate?.plan,
                        section: asset.details.realEstate?.section,
                        futureUsageType_Name: asset.details.realEstate?.futureUsage,
                        hasPropertyBeenSold: asset.details.realEstate?.hasPropertyBeenSold === "yes",
                        soldDate: soldDate,
                        rentalIncome: asset.details.realEstate?.rentalIncome,
                        id: asset.id,
                        assetCategoryType_Name: asset.assetCategory,
                        assetType_Name: asset.assetType,
                        origin_Name: "manual",
                        value: asset.value,
                        description: asset.description,
                        downPaymentValue: asset.downPaymentValue,
                        applicants: asset.applicants,
                    }));
                    break;
                case "lifeinsurance":
                    lifeInsurance.push(new LifeInsuranceDto({
                        cashSurrenderValue: asset.details.lifeInsurance?.cashSurrenderValue,
                        id: asset.id,
                        assetCategoryType_Name: asset.assetCategory,
                        assetType_Name: asset.assetType,
                        origin_Name: "manual",
                        value: asset.value,
                        description: asset.description,
                        downPaymentValue: asset.downPaymentValue,
                        applicants: asset.applicants,
                    }));
                    break;
                case "other":
                    other.push(new AssetDto({
                        id: asset.id,
                        assetCategoryType_Name: asset.assetCategory,
                        assetType_Name: "other",
                        origin_Name: "manual",
                        value: asset.value,
                        description: asset.description,
                        downPaymentValue: asset.downPaymentValue,
                        applicants: asset.applicants,
                    }));
                    break;
                default:
                    downPayment.push(new AssetDto({
                        id: asset.id,
                        assetCategoryType_Name: "downpayment",
                        assetType_Name: asset.assetType,
                        origin_Name: "manual",
                        value: 0,
                        description: asset.description,
                        downPaymentValue: asset.value,
                        applicants: asset.applicants,
                    }));
                    break
            }
        });

        await client.apiAssetsPost(new DealAssetsDto({
            nonRegistered: nonRegistered,
            registered: registered,
            realEstate: realEstate,
            vehicle: vehicle,
            lifeInsurance: lifeInsurance,
            other: other,
            downPayment: downPayment,
        })).then(async (result: DealAssetsDto) => {
            // Handle the result if needed
            setIsSaving(false);
            console.log(result);
            if (result.nonRegistered && result.nonRegistered.length > 0) {
                result.nonRegistered.forEach((asset: FinancialAssetDto) => {
                    assets.forEach((a: assetInfo) => {
                        if (a.details.nonRegistered && a.details.nonRegistered.financialInstitution === asset.fiName &&
                            a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
                            let newAsset = { ...a };
                            newAsset.id = asset.id ? asset.id : 0;
                            dispatch(setAssetInfo(newAsset));
                        }
                    });
                });
            }
            if (result.registered && result.registered.length > 0) {
                result.registered.forEach((asset: FinancialAssetDto) => {
                    assets.forEach((a: assetInfo) => {
                        if (a.details.registered && a.details.registered.financialInstitution === asset.fiName &&
                            a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
                            let newAsset = { ...a };
                            newAsset.id = asset.id ? asset.id : 0;
                            dispatch(setAssetInfo(newAsset));
                        }
                    });
                });
            }
            if (result.realEstate && result.realEstate.length > 0) {
                let expenseList: PropertyExpenseDto[] = [];
                result.realEstate.forEach(async (asset: RealEstateDto) => {

                    let existingExpense = expenses.find(exp => exp.assetId === asset.id);
                    let existingAsset = assets.find(a => a.details.realEstate && a.details.realEstate.occupancyType === asset.occupancyType_Name &&
                        a.value === asset.value && a.downPaymentValue === asset.downPaymentValue);

                    //Append Property Tax as Expense for Real Estate Asset
                    let propertyExpenseDto = new PropertyExpenseDto({
                        assetId: asset.id ? asset.id : 0,
                        expenseCategoryType: "property",
                        taxAmount: existingAsset && existingAsset.details.realEstate && existingAsset.details.realEstate.propertyTax ? existingAsset.details.realEstate.propertyTax : 0,
                        taxFrequency: "annually",
                        condoFeesAmount: existingAsset && existingAsset.details.realEstate && existingAsset.details.realEstate.maintenanceFee ? existingAsset.details.realEstate.maintenanceFee : 0,
                        condoFeesFrequency: "monthly",
                        utilityFeesAmount: existingAsset && existingAsset.details.realEstate && existingAsset.details.realEstate.heatingCost ? existingAsset.details.realEstate.heatingCost : 0,
                        utilityFeesFrequency: "monthly",
                        ownershipPercentage: existingAsset && existingAsset.details.realEstate && existingAsset.details.realEstate.ownershipPercentage ? +existingAsset.details.realEstate.ownershipPercentage : 0,
                        id: existingExpense ? existingExpense.id : 0,
                        applicants: realEstate[0].applicants
                    });
                    expenseList.push(propertyExpenseDto);
                    assets.forEach((a: assetInfo) => {
                        if (a.details.realEstate && a.details.realEstate.occupancyType === asset.occupancyType_Name &&
                            a.value === asset.value && a.downPaymentValue === asset.downPaymentValue && a.id == 0) {
                            let newAsset = { ...a };
                            newAsset.id = asset.id ? asset.id : 0;
                            dispatch(setAssetInfo(newAsset));
                        }
                    });
                });
                await client.apiExpensesPost(new DealExpensesDto({
                    property: expenseList,
                })).then((expenseResult) => {
                    if (expenseResult.property && expenseResult.property.length > 0) {
                        expenseResult.property.forEach((expense: PropertyExpenseDto) => {
                            let existingExpense = expenses.find(exp => exp.id === expense.id);
                            if (existingExpense == undefined) {
                                let newExpense: expenseInfo = {
                                    displayId: (expenses.length + 1).toString(),
                                    id: expense.id ? expense.id : 0,
                                    assetId: expense.assetId ? expense.assetId : 0,
                                    expenseCategory: "property",
                                    propertyTax: expense.taxAmount ? expense.taxAmount : 0,
                                    heatingCost: expense.utilityFeesAmount ? expense.utilityFeesAmount : 0,
                                    maintenanceFee: expense.condoFeesAmount ? expense.condoFeesAmount : 0,
                                    ownershipPercentage: expense.ownershipPercentage ? expense.ownershipPercentage : 0,
                                    applicants: expense.applicants ? expense.applicants : [],
                                };
                                dispatch(addExpense(newExpense));
                            } else {
                                let updateExpense = { ...existingExpense };
                                updateExpense.propertyTax = expense.taxAmount ? expense.taxAmount : 0;
                                updateExpense.heatingCost = expense.utilityFeesAmount ? expense.utilityFeesAmount : 0;
                                updateExpense.maintenanceFee = expense.condoFeesAmount ? expense.condoFeesAmount : 0;
                                updateExpense.ownershipPercentage = expense.ownershipPercentage ? expense.ownershipPercentage : 0;
                                dispatch(setExpenseInfo(updateExpense));
                            }
                        });
                        //Remove expenses that are no longer linked to any assets
                        let removableExpenses: number[] = [];
                        expenses.forEach(exp => {
                            let validExpense = false;
                            expenseResult.property?.forEach((expense: PropertyExpenseDto) => {
                                if (exp.id === expense.id) {
                                    validExpense = true;
                                }
                            });
                            if (!validExpense) {
                                removableExpenses.push(exp.id);
                            }
                        });
                        removableExpenses.forEach(id => {
                            dispatch(removeExpense(id));
                        });
                    }

                }).catch((error) => {
                    console.log("Error saving property expense for asset");
                });
            }
            if (result.vehicle && result.vehicle.length > 0) {
                result.vehicle.forEach((asset: VehicleDto) => {
                    assets.forEach((a: assetInfo) => {
                        if (a.details.vehicle && a.details.vehicle.ownership === asset.vehicleOwnershipType_Name && a.details.vehicle.make === asset.make &&
                            a.details.vehicle.model === asset.model && a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
                            let newAsset = { ...a };
                            newAsset.id = asset.id ? asset.id : 0;
                            dispatch(setAssetInfo(newAsset));
                        }
                    });
                });
            }
            if (result.lifeInsurance && result.lifeInsurance.length > 0) {
                result.lifeInsurance.forEach((asset: LifeInsuranceDto) => {
                    assets.forEach((a: assetInfo) => {
                        if (a.details.lifeInsurance && a.details.lifeInsurance.cashSurrenderValue === asset.cashSurrenderValue &&
                            a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
                            let newAsset = { ...a };
                            newAsset.id = asset.id ? asset.id : 0;
                            dispatch(setAssetInfo(newAsset));
                        }
                    });
                });
            }
            if (result.downPayment && result.downPayment.length > 0) {
                result.downPayment.forEach((asset: AssetDto) => {
                    assets.forEach((a: assetInfo) => {
                        if (a.assetType === asset.assetType_Name && a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
                            let newAsset = { ...a };
                            newAsset.id = asset.id ? asset.id : 0;
                            dispatch(setAssetInfo(newAsset));
                        }
                    });
                });
            }
            if (result.other && result.other.length > 0) {
                result.other.forEach((asset: AssetDto) => {
                    assets.forEach((a: assetInfo) => {
                        if (a.assetCategory === asset.assetCategoryType_Name && a.value === asset.value && a.downPaymentValue === asset.downPaymentValue) {
                            let newAsset = { ...a };
                            newAsset.id = asset.id ? asset.id : 0;
                            dispatch(setAssetInfo(newAsset));
                        }
                    });
                });
            }
            dispatch(setApplicationProgress(13));
            if (editMode) {
                navigate(-1);
            } else {
                navigate("/single/property-details");
            }
        }).catch((error) => {
            setIsSaving(false);
            console.log("Error saving asset details", error);
            dispatch(setApplicationProgress(13));
            if (editMode) {
                navigate(-1);
            } else {
                navigate("/single/property-details");
            }
        });


    };

    useEffect(() => {
        let newAssetErrors: Record<string, string>[] = [];
        let outstandingErrors = false;
        assets.forEach((asset: assetInfo, index: any) => {
            let id = index + 1;
            //Only render Canada Post Autocomplete if visible (to improve rendering performance)
            if (asset.visible && asset.origin !== "internal" && asset.assetCategory === "realestate") {
                let fields = [
                    {
                        element: "realEstateAddressSearch" + id,
                        field: "",
                        mode: window.pca?.fieldMode.SEARCH,
                    },
                    {
                        element: "span-street-address-" + id,
                        field: "Line1",
                        mode: window.pca?.fieldMode.POPULATE,
                    },
                    {
                        element: "span-street-address2-" + id,
                        field: "Line2",
                        mode: window.pca?.fieldMode?.POPULATE,
                    },
                    { element: "city-" + id, field: "City", mode: window.pca?.fieldMode.POPULATE },
                    {
                        element: "span-state-" + id,
                        field: "ProvinceName",
                        mode: window.pca?.fieldMode.POPULATE,
                    },
                    {
                        element: "",
                        field: "PostalCode",
                        mode: window.pca?.fieldMode.POPULATE,
                    },
                    {
                        element: "span-country-" + id,
                        field: "CountryName",
                        mode: window.pca?.fieldMode.POPULATE,
                    },
                ];
                let control = new window.pca.Address(fields, options);
                control.listen("populate", (address: any) => {
                    let streetInfo = address.Line1.split(" ");
                    let streetName = "";
                    let streetDirection = "";
                    let streetType = "";
                    let streetComponents: string[] = address.Street.split(" ");
                    let lastStreetComponent = streetComponents[streetComponents.length - 1].toUpperCase();

                    //Check if street direction and type exists in the street name
                    if (streetDirections.includes(lastStreetComponent)) {
                        streetDirection = lastStreetComponent;
                        let secondLastStreetComponent = streetComponents[streetComponents.length - 2].toUpperCase();
                        if (streetTypes.includes(secondLastStreetComponent)) {
                            streetType = secondLastStreetComponent;
                        }
                        streetComponents.forEach((value, index) => {
                            if (index < streetComponents.length - 2) {
                                streetName += value + " "
                            }
                        });
                    } else if (streetTypes.includes(lastStreetComponent)) {
                        streetType = lastStreetComponent;
                        streetComponents.forEach((value, index) => {
                            if (index != streetComponents.length - 1) {
                                streetName += value + " "
                            }
                        });
                    } else {
                        streetComponents.forEach((value, index) => {
                            console.log(value, index);
                            streetName += value + " "
                        });
                    }
                    streetName = streetName.trim();

                    //Check if unit number is embeded in the street number
                    let streetNumber = "";
                    let streetNumberComponents = streetInfo[0].split("-");
                    if (streetNumberComponents.length > 1) {
                        streetNumber = streetNumberComponents[streetNumberComponents.length - 1];
                    } else {
                        streetNumber = streetInfo[0];
                    }

                    let newAssetInfo: assetInfo = { ...asset }

                    let newDetails = { ...newAssetInfo.details };
                    if (newAssetInfo.assetCategory === "realestate") {
                        let realEstateDetails = { ...newDetails.realEstate };
                        realEstateDetails.address = {
                            displayId: asset.displayId,
                            streetNumber: streetNumber,
                            streetName: streetName,
                            streetType: streetType,
                            streetDirection: streetDirection,
                            city: address.City,
                            province: address.ProvinceCode,
                            country: address.CountryName,
                            postalCode: address.PostalCode,
                            unitType: address.UnitType,
                            unitNumber: address.SubBuilding != undefined ? address.SubBuilding : ""
                        }
                        //If Street Type is not in the master list, toggle manual address entry
                        if (!streetTypes.includes(realEstateDetails?.address?.streetType)) {
                            realEstateDetails.manualAddressEntered = true;
                        }
                        setRealEstateAddress(asset.displayId, realEstateDetails.address, realEstateDetails.manualAddressEntered);
                    }

                    if (assetErrors[parseInt(id) - 1]["autoAddress"] != undefined) {
                        let newAssetErrors = [...assetErrors];
                        delete newAssetErrors[parseInt(id) - 1]["autoAddress"];
                        delete newAssetErrors[parseInt(id) - 1]["streetNumber"];
                        delete newAssetErrors[parseInt(id) - 1]["streetName"];
                        delete newAssetErrors[parseInt(id) - 1]["streetType"];
                        delete newAssetErrors[parseInt(id) - 1]["streetDirection"];
                        delete newAssetErrors[parseInt(id) - 1]["city"];
                        delete newAssetErrors[parseInt(id) - 1]["province"];
                        delete newAssetErrors[parseInt(id) - 1]["postalCode"];
                        delete newAssetErrors[parseInt(id) - 1]["country"];
                        setAssetErrors(newAssetErrors);
                    }
                });
            }
            let errors: Record<string, string> = assetErrors[index] ? assetErrors[index] : {};
            newAssetErrors.push(errors);

            if (Object.keys(errors).length > 0) {
                outstandingErrors = true;
            }
        });
        setAssetErrors(newAssetErrors);
        setDisplayError(outstandingErrors);

    }, [assets])

    const addCurrAsset = () => {
        let assetId = (assets.length + 1).toString();

        //Auto hide other assets (if they're visible)
        assets.forEach(a => {
            if (a.displayId != assetId) {
                let newAssetInfo = { ...a }
                newAssetInfo.visible = false;
                dispatch(setAssetInfo(newAssetInfo));
            }
        });

        let newAssetInfo: assetInfo = {
            displayId: assetId,
            id: 0,
            visible: true,
            assetCategory: "",
            assetType: "",
            origin: "",
            value: 0,
            valueText: "",
            description: "",
            downPaymentValue: 0,
            downPaymentValueText: "",
            applicants: [],
            details: {
                nonRegistered: undefined,
                registered: undefined,
                realEstate: undefined,
                vehicle: undefined,
                lifeInsurance: undefined
            }
        };
        dispatch(addAsset(newAssetInfo));
    }

    const toggleAssetVisibility = (id: string) => {
        const idx = assets.findIndex(asset => asset.displayId === id);
        if (idx !== -1) {
            dispatch(toggleAssetDisplay(idx));
        }
    }

    /*Reusable Inputs */

    const assetValue = (position: any, leadingQuestion: boolean, questionType: number) => {
        return (
            <div className={leadingQuestion ? clsx(style.twoColFirst, "mt-5") : "mt-5"}>
                <label htmlFor={position.displayId + "assetValue"}>{questionType === 1 ? "Policy Coverage Amount" : questionType == 2 ? "What is the value?" : "Value"}</label>
                <div className="flex relative">
                    <span>
                        <img
                            src="assets/error-icon.svg"
                            className={clsx(
                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["assetValue"] != undefined
                                    ? ""
                                    : "hidden",
                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                        />
                    </span>
                    <DebouncedCurrencyInput
                        id={position.displayId + "assetValue"}
                        placeholder="$"
                        prefix={"$"}
                        decimalsLimit={2}
                        decimalScale={2}
                        allowNegativeValue={false}
                        maxLength={9}
                        value={position.valueText}
                        disabled={position.origin === "internal"}
                        className={clsx(
                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["assetValue"] != undefined
                                ? "error"
                                : "",
                            "mt-2 w:[220px] md:w-[305px]"
                        )}
                        onCommit={(val: string | undefined) => setAssetValue(position.displayId, val ?? "")}
                    />
                </div>
                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["assetValue"] != undefined && (
                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {assetErrors[position.displayId - 1]["assetValue"]}
                    </span>
                )}
            </div>
        )
    }

    const assetOwnership = (position: any, leadingQuestion: boolean) => {
        return (
            <div className={leadingQuestion ? clsx(style.twoColFirst, "mt-5") : "mt-5"}>
                <label htmlFor="assetOwnership">Who owns this asset?</label>
                <div className="flex relative">
                    <span>
                        <img
                            src="assets/error-icon.svg"
                            className={clsx(
                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["ownership"] != undefined
                                    ? ""
                                    : "hidden",
                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                        />
                    </span>
                    <select
                        id="ownership"
                        onChange={(e) => {
                            let owners = [];
                            owners.push(+e.target.value);
                            setAssetOwnership(position.displayId, owners)
                        }}
                        value={position.applicants.length > 0 ? position.applicants[0] : ""}
                        disabled={position.origin === "internal"}
                        className={clsx(
                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["ownership"] != undefined
                                ? "error"
                                : "",
                            "mt-2 w:[220px] md:w-[305px]"
                        )}
                        style={{ alignItems: 'center' }}
                    >
                        <option hidden value="">
                            Choose...
                        </option>
                        {applicantList.map((applicant) => (
                            <option key={applicant.applicantId} value={applicant.applicantId}>
                                {applicant.fullname}
                            </option>
                        ))}
                    </select>
                </div>
                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["ownership"] != undefined && (
                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {assetErrors[position.displayId - 1]["ownership"]}
                    </span>
                )}
            </div>
        )
    }

    const assetUtilization = (position: any, leadingQuestion: boolean) => {
        return (
            <div className={leadingQuestion ? clsx(style.twoColFirst, "mt-5") : "mt-5"}>
                <label htmlFor={position.displayId + "assetDownPayment"}>How much will you use?</label>
                <div className="flex relative">
                    <span>
                        <img
                            src="assets/error-icon.svg"
                            className={clsx(
                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["assetDownPayment"] != undefined
                                    ? ""
                                    : "hidden",
                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                        />
                    </span>
                    <DebouncedCurrencyInput
                        id={position.displayId + "assetDownPayment"}
                        placeholder="$"
                        prefix={"$"}
                        decimalsLimit={2}
                        decimalScale={2}
                        allowNegativeValue={false}
                        maxLength={9}
                        value={position.downPaymentValueText}
                        className={clsx(
                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["assetDownPayment"] != undefined
                                ? "error"
                                : "",
                            "mt-2 w:[220px] md:w-[305px]"
                        )}
                        onCommit={(val: string | undefined) => setAssetDownPaymentValue(position.displayId, val ?? "")}
                    />
                </div>
                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["assetDownPayment"] != undefined && (
                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {assetErrors[position.displayId - 1]["assetDownPayment"]}
                    </span>
                )}
            </div>
        )
    }

    const assetDescription = (position: any, leadingQuestion: boolean, isOptional: boolean) => {
        return (
            <div className={leadingQuestion ? clsx(style.twoColFirst, "mt-5") : "mt-5"}>
                <label htmlFor="assetDescription">Description {isOptional && "(optional)"}</label>
                <div className="flex relative">
                    <span>
                        <img
                            src="assets/error-icon.svg"
                            className={clsx(
                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["assetDescription"] != undefined
                                    ? ""
                                    : "hidden",
                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                        />
                    </span>
                    <DebouncedTextInput
                        className={clsx(
                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["assetDescription"] != undefined
                                ? "error"
                                : "",
                            "mt-2 w:[220px] md:w-[305px]"
                        )}
                        type="text"
                        id={"assetDescription" + position.displayId}
                        value={position.description}
                        maxLength={30}
                        onCommit={(val) => setAssetDescription(position.displayId, val)}
                    />
                </div>
                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["assetDescription"] != undefined && (
                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {assetErrors[position.displayId - 1]["assetDescription"]}
                    </span>
                )}
            </div>
        )
    }

    const rentalMonthlyIncome = (position: any, leadingQuestion: boolean) => {
        return (
            <div className={leadingQuestion ? clsx(style.twoColFirst, "mt-5") : "mt-5"}>
                <label htmlFor="rentalIncome">Monthly rental income</label>
                <div className="flex relative">
                    <span>
                        <img
                            src="assets/error-icon.svg"
                            className={clsx(
                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["rentalIncome"] != undefined
                                    ? ""
                                    : "hidden",
                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                        />
                    </span>
                    <DebouncedCurrencyInput
                        id={position.displayId + "rentalIncome"}
                        placeholder="$"
                        prefix={"$"}
                        decimalsLimit={2}
                        decimalScale={2}
                        allowNegativeValue={false}
                        maxLength={9}
                        value={position.assetCategory === "realestate" ? position.details?.realEstate?.rentalIncomeText : ""}
                        className={clsx(
                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["rentalIncome"] != undefined
                                ? "error"
                                : "",
                            "mt-2 w:[220px] md:w-[305px]"
                        )}
                        onCommit={(val: string | undefined) => setRentalIncomeValue(position.displayId, val ?? "")}
                    />
                </div>
                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["rentalIncome"] != undefined && (
                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {assetErrors[position.displayId - 1]["rentalIncome"]}
                    </span>
                )}
            </div>)
    }

    const rentalUnitCount = (position: any, leadingQuestion: boolean) => {
        return (<div className={leadingQuestion ? clsx(style.twoColFirst, "mt-5") : "mt-5"}>
            <label htmlFor="numOfProperties">Number of Units (Optional)</label>
            <div className="flex relative">
                <span>
                    <img
                        src="assets/error-icon.svg"
                        className={clsx(
                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["numOfProperties"] != undefined
                                ? ""
                                : "hidden",
                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                        )}
                        alt="error"
                        height={5}
                        width={5}
                    />
                </span>
                <DebouncedTextInput
                    className={clsx(
                        assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["numOfProperties"] != undefined
                            ? "error"
                            : "",
                        "mt-2 w:[220px] md:w-[305px]"
                    )}
                    type="number"
                    id={"numOfProperties" + position.displayId}
                    value={position.details?.realEstate?.numOfProperties}
                    maxLength={30}
                    onFocus={(e) => e.target.select()}
                    onCommit={(val) => setNumOfProperties(position.displayId, val)}
                />
            </div>
            {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["numOfProperties"] != undefined && (
                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                    {assetErrors[position.displayId - 1]["numOfProperties"]}
                </span>
            )}
        </div>)
    }


    /*
    Update operations for all Asset Types
    */

    const setAssetCategory = (id: string, value: string) => {
        const idx = assets.findIndex(a => a.displayId === id);
        if (idx === -1) return;
        const current = assets[idx];
        if (current.assetCategory === value) return;
        let newAssetInfo: assetInfo = { ...current };
        newAssetInfo.details = {
            nonRegistered: value === "nonregistered" ? {
                financialInstitution: "",
                otherFinancialInstitution: ""
            } : undefined,
            registered: value === "registered" ? {
                financialInstitution: "",
                otherFinancialInstitution: ""
            } : undefined,
            realEstate: value === "realestate" ? {
                occupancyType: "",
                propertyType: "",
                rentalComponent: "",
                numOfProperties: "",
                ownershipPercentage: "",
                address: {
                    displayId: id,
                    streetNumber: "",
                    streetName: "",
                    streetType: "",
                    streetDirection: "",
                    unitType: "",
                    unitNumber: "",
                    city: "",
                    province: "",
                    postalCode: "",
                    country: ""
                },
                originalPurchasePrice: 0,
                originalPurchasePriceText: "",
                originalPurchaseDate: "",
                propertyTax: 0,
                propertyTaxText: "",
                heatingCost: 0,
                heatingCostText: "",
                maintenanceFee: 0,
                maintenanceFeeText: "",
                lot: "",
                block: "",
                plan: "",
                section: "",
                futureUsage: "",
                hasPropertyBeenSold: "",
                soldDate: "",
                rentalIncome: 0,
                rentalIncomeText: "",
                manualAddressEntered: false,
                isCurrentResidence: false
            } : undefined,
            vehicle: value === "vehicle" ? {
                ownership: "",
                year: "",
                make: "",
                model: ""
            } : undefined,
            lifeInsurance: value === "lifeinsurance" ? {
                cashSurrenderValue: 0,
                cashSurrenderValueText: ""
            } : undefined
        };
        newAssetInfo.assetCategory = value;
        newAssetInfo.id = 0;
        newAssetInfo.value = 0;
        newAssetInfo.valueText = "";
        newAssetInfo.downPaymentValue = 0;
        newAssetInfo.downPaymentValueText = "";
        newAssetInfo.description = "";
        newAssetInfo.applicants = [];

        if (value === "gift" || value === "depositonpurchase" || value === "builderdeposit"
            || value === "inheritance" || value === "equalization" || value === "employerrelocation") {
            newAssetInfo.assetType = value;
        } else {
            newAssetInfo.assetType = "";
        }

        dispatch(setAssetInfo(newAssetInfo));

        if (assetErrors[parseInt(id) - 1]["assetCategory"] != undefined && value.trim() != "") {
            let newAssetErrors = [...assetErrors];
            delete newAssetErrors[parseInt(id) - 1]["assetCategory"];
            setAssetErrors(newAssetErrors);
        }
    }

    const setAssetType = (id: string, value: string) => {
        const idx = assets.findIndex(a => a.displayId === id);
        if (idx === -1) return;
        const current = assets[idx];
        if ((current.assetType ?? "") === (value ?? "")) return;
        const newAssetInfo: assetInfo = { ...current, assetType: value };
        dispatch(setAssetInfo(newAssetInfo));

        if (assetErrors[parseInt(id) - 1]["assetType"] != undefined && value.trim() != "") {
            let newAssetErrors = [...assetErrors];
            delete newAssetErrors[parseInt(id) - 1]["assetType"];
            setAssetErrors(newAssetErrors);
        }
    }

    const setAssetValue = (id: string, newAmount: string) => {
        const idx = assets.findIndex(a => a.displayId === id);
        if (idx === -1) return;
        const current = assets[idx];
        const clean = (newAmount ?? "").replace(/[^0-9.]/g, "");
        const nextText = clean;
        const nextValue = clean ? +clean : 0;
        if (current.valueText === nextText && current.value === nextValue) return;
        const newAssetInfo: assetInfo = { ...current, valueText: nextText, value: nextValue };
        dispatch(setAssetInfo(newAssetInfo));

        if (assetErrors[parseInt(id) - 1]["assetValue"] != undefined && newAmount.trim() != "") {
            let newAssetErrors = [...assetErrors];
            delete newAssetErrors[parseInt(id) - 1]["assetValue"];
            setAssetErrors(newAssetErrors);
        }
    };

    const setAssetDownPaymentValue = (id: string, newAmount: string) => {
        const idx = assets.findIndex(a => a.displayId === id);
        if (idx === -1) return;
        const current = assets[idx];
        const clean = (newAmount ?? "").replace(/[^0-9.]/g, "");
        const nextText = clean;
        const nextValue = clean ? +clean : 0;
        if (current.downPaymentValueText === nextText && current.downPaymentValue === nextValue) return;
        const newAssetInfo: assetInfo = { ...current, downPaymentValueText: nextText, downPaymentValue: nextValue };
        dispatch(setAssetInfo(newAssetInfo));

        if (assetErrors[parseInt(id) - 1]["assetDownPayment"] != undefined && newAmount.trim() != "") {
            let newAssetErrors = [...assetErrors];
            delete newAssetErrors[parseInt(id) - 1]["assetDownPayment"];
            setAssetErrors(newAssetErrors);
        }
    };

    const setAssetOwnership = (id: string, owners: number[]) => {
        const idx = assets.findIndex(a => a.displayId === id);
        if (idx === -1) return;
        const current = assets[idx];
        const currOwners = current.applicants ?? [];
        const same = currOwners.length === owners.length && currOwners.every((o, i) => o === owners[i]);
        if (same) return;
        const newAssetInfo: assetInfo = { ...current, applicants: owners };
        dispatch(setAssetInfo(newAssetInfo));

        if (assetErrors[parseInt(id) - 1]["ownership"] != undefined && owners.length > 0) {
            let newAssetErrors = [...assetErrors];
            delete newAssetErrors[parseInt(id) - 1]["ownership"];
            setAssetErrors(newAssetErrors);
        }
    }

    const setAssetDescription = (id: string, value: string) => {
        const idx = assets.findIndex(a => a.displayId === id);
        if (idx === -1) return;
        const current = assets[idx];
        if ((current.description ?? "") === (value ?? "")) return;
        const newAssetInfo: assetInfo = { ...current, description: value ?? "" };
        dispatch(setAssetInfo(newAssetInfo));

        if (assetErrors[parseInt(id) - 1]["assetDescription"] != undefined && value.trim() != "") {
            let newAssetErrors = [...assetErrors];
            delete newAssetErrors[parseInt(id) - 1]["assetDescription"];
            setAssetErrors(newAssetErrors);
        }
    }

    /*
    Update operations for Registered & NonRegistered Asset Types
    */

    const setFinancialInstitution = (id: string, value: string) => {
        const idx = assets.findIndex(a => a.displayId === id);
        if (idx === -1) return;
        const current = assets[idx];
        const newDetails = { ...current.details };
        if (current.assetCategory === "nonregistered") {
            const curr = newDetails.nonRegistered?.financialInstitution ?? "";
            if (curr === value && (newDetails.nonRegistered?.otherFinancialInstitution ?? "") === "") return;
            newDetails.nonRegistered = {
                financialInstitution: value,
                otherFinancialInstitution: ""
            };
        } else {
            const curr = newDetails.registered?.financialInstitution ?? "";
            if (curr === value && (newDetails.registered?.otherFinancialInstitution ?? "") === "") return;
            newDetails.registered = {
                financialInstitution: value,
                otherFinancialInstitution: ""
            };
        }
        const newAssetInfo: assetInfo = { ...current, details: newDetails };
        dispatch(setAssetInfo(newAssetInfo));

        if (assetErrors[parseInt(id) - 1]["fiName"] != undefined && value.trim() != "") {
            let newAssetErrors = [...assetErrors];
            delete newAssetErrors[parseInt(id) - 1]["fiName"];
            setAssetErrors(newAssetErrors);
        }
    }

    const setOtherFinancialInstitution = (id: string, value: string) => {
        const idx = assets.findIndex(a => a.displayId === id);
        if (idx === -1) return;
        const current = assets[idx];
        const newDetails = { ...current.details };
        if (current.assetCategory === "nonregistered") {
            const curr = newDetails.nonRegistered?.otherFinancialInstitution ?? "";
            if (curr === value && (newDetails.nonRegistered?.financialInstitution ?? "") === "other") return;
            newDetails.nonRegistered = {
                financialInstitution: "other",
                otherFinancialInstitution: value
            };
        } else {
            const curr = newDetails.registered?.otherFinancialInstitution ?? "";
            if (curr === value && (newDetails.registered?.financialInstitution ?? "") === "other") return;
            newDetails.registered = {
                financialInstitution: "other",
                otherFinancialInstitution: value
            };
        }
        const newAssetInfo: assetInfo = { ...current, details: newDetails };
        dispatch(setAssetInfo(newAssetInfo));

        if (assetErrors[parseInt(id) - 1]["fiOtherName"] != undefined && value.trim() != "") {
            let newAssetErrors = [...assetErrors];
            delete newAssetErrors[parseInt(id) - 1]["fiOtherName"];
            setAssetErrors(newAssetErrors);
        }
    }

    /*
    Update operations for Vehicle Asset Types
    */

    const setVehicle = (id: string, year: string, make: string, model: string) => {
        const idx = assets.findIndex(a => a.displayId === id);
        if (idx === -1) return;
        const current = assets[idx];
        if (current.assetCategory !== "vehicle") return;
        const curr = current.details?.vehicle ?? { ownership: "", year: "", make: "", model: "" };
        const nextYear = year ?? "";
        const nextMake = make ?? "";
        const nextModel = model ?? "";
        if ((curr.year ?? "") === nextYear && (curr.make ?? "") === nextMake && (curr.model ?? "") === nextModel) return;
        const newDetails = { ...current.details };
        newDetails.vehicle = {
            ownership: curr.ownership ? curr.ownership : "",
            year: nextYear,
            make: nextMake,
            model: nextModel
        };
        const newAssetInfo: assetInfo = { ...current, details: newDetails };
        dispatch(setAssetInfo(newAssetInfo));

        if (assetErrors[parseInt(id) - 1]["vehicleYear"] != undefined && year.trim() != "") {
            let newAssetErrors = [...assetErrors];
            delete newAssetErrors[parseInt(id) - 1]["vehicleYear"];
            setAssetErrors(newAssetErrors);
        }
        if (assetErrors[parseInt(id) - 1]["vehicleMake"] != undefined && make.trim() != "") {
            let newAssetErrors = [...assetErrors];
            delete newAssetErrors[parseInt(id) - 1]["vehicleMake"];
            setAssetErrors(newAssetErrors);
        }
        if (assetErrors[parseInt(id) - 1]["vehicleModel"] != undefined && model.trim() != "") {
            let newAssetErrors = [...assetErrors];
            delete newAssetErrors[parseInt(id) - 1]["vehicleModel"];
            setAssetErrors(newAssetErrors);
        }
    }

    const setVehicleOwnership = (id: string, value: string) => {
        const idx = assets.findIndex(a => a.displayId === id);
        if (idx === -1) return;
        const current = assets[idx];
        if (current.assetCategory !== "vehicle") return;
        const currOwnership = current.details?.vehicle?.ownership ?? "";
        const nextOwnership = value ?? "";
        if (currOwnership === nextOwnership) return;
        const newDetails = { ...current.details };
        const vehicleDetails = { ...newDetails.vehicle };
        newDetails.vehicle = {
            ownership: nextOwnership,
            year: vehicleDetails.year ? vehicleDetails.year : "",
            make: vehicleDetails.make ? vehicleDetails.make : "",
            model: vehicleDetails.model ? vehicleDetails.model : ""
        };
        const newAssetInfo: assetInfo = { ...current, details: newDetails };
        dispatch(setAssetInfo(newAssetInfo));

        if (assetErrors[parseInt(id) - 1]["ownershipType"] != undefined && value.trim() != "") {
            let newAssetErrors = [...assetErrors];
            delete newAssetErrors[parseInt(id) - 1]["ownershipType"];
            setAssetErrors(newAssetErrors);
        }
    }


    /*
    Update operations for Real Estate Asset Types
    */

    const toggleManualAddress = (id: string) => {
        assets.forEach(a => {
            if (a.displayId == id) {
                let newAssetInfo: assetInfo = { ...a }
                let newDetails = { ...newAssetInfo.details };
                if (newAssetInfo.assetCategory === "realestate") {
                    let realEstateDetails = { ...newAssetInfo.details.realEstate };
                    newDetails.realEstate = {
                        occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
                        propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
                        rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
                        numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
                        ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
                        address: {
                            displayId: id,
                            streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                            streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                            streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                            streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                            unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                            unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                            city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                            province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                            postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                            country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
                        },
                        originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
                        originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
                        originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
                        propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
                        propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
                        heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
                        heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
                        maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
                        maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
                        lot: realEstateDetails.lot ? realEstateDetails.lot : "",
                        block: realEstateDetails.block ? realEstateDetails.block : "",
                        plan: realEstateDetails.plan ? realEstateDetails.plan : "",
                        section: realEstateDetails.section ? realEstateDetails.section : "",
                        futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
                        hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
                        soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
                        rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
                        rentalIncomeText: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome.toString() : "",
                        manualAddressEntered: !realEstateDetails.manualAddressEntered,
                        isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
                    }
                }
                newAssetInfo.details = newDetails;
                dispatch(setAssetInfo(newAssetInfo));
            }
        });
    };

    const getAddressData = (id: any, type: any, val: any) => {
        let addressData = { ...assets[id - 1] };

        let newAssetAddressData = { ...addressData.details?.realEstate?.address }

        switch (type) {
            case "streetNumber":
                newAssetAddressData.streetNumber = val;
                break;
            case "streetName":
                newAssetAddressData.streetName = val;
                break;
            case "streetType":
                newAssetAddressData.streetType = val;
                break;
            case "streetDirection":
                newAssetAddressData.streetDirection = val;
                break;
            case "unitType":
                newAssetAddressData.unitType = val;
                break;
            case "unitNumber":
                newAssetAddressData.unitNumber = val;
                break;
            case "cityName":
                newAssetAddressData.city = val;
                break;
            case "province":
                newAssetAddressData.province = val;
                break;
            case "postalCode":
                newAssetAddressData.postalCode = val;
                break;
            case "country":
                newAssetAddressData.country = val;
                //Clear the province/state if country is updated
                newAssetAddressData.province = "";
                break;
        }
        setRealEstateAddress(addressData.displayId, newAssetAddressData);
    }

    const setRealEstateAddress = (id: string, newAddress: any, manualAddressEntered?: boolean) => {
        assets.forEach(a => {
            if (a.displayId == id) {
                let newAssetInfo: assetInfo = { ...a }
                let newDetails = { ...newAssetInfo.details };
                if (newAssetInfo.assetCategory === "realestate") {
                    let realEstateDetails = { ...newAssetInfo.details.realEstate };
                    realEstateDetails.address = newAddress;
                    newDetails.realEstate = {
                        occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
                        propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
                        rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
                        numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
                        ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
                        address: {
                            displayId: id,
                            streetNumber: newAddress.streetNumber ? newAddress.streetNumber : "",
                            streetName: newAddress.streetName ? newAddress.streetName : "",
                            streetType: newAddress.streetType ? newAddress.streetType : "",
                            streetDirection: newAddress.streetDirection ? newAddress.streetDirection : "",
                            unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                            unitNumber: newAddress.unitNumber ? newAddress.unitNumber : "",
                            city: newAddress.city ? newAddress.city : "",
                            province: newAddress.province ? newAddress.province : "",
                            postalCode: newAddress.postalCode ? newAddress.postalCode : "",
                            country: newAddress.country ? newAddress.country : ""
                        },
                        originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
                        originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
                        originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
                        propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
                        propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
                        heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
                        heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
                        maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
                        maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
                        lot: realEstateDetails.lot ? realEstateDetails.lot : "",
                        block: realEstateDetails.block ? realEstateDetails.block : "",
                        plan: realEstateDetails.plan ? realEstateDetails.plan : "",
                        section: realEstateDetails.section ? realEstateDetails.section : "",
                        futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
                        hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
                        soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
                        rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
                        rentalIncomeText: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome.toString() : "",
                        manualAddressEntered: manualAddressEntered ? manualAddressEntered : realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
                        isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
                    }
                }
                newAssetInfo.details = newDetails;
                dispatch(setAssetInfo(newAssetInfo));
            }
        });
    };

    const setRealEstateLot = (id: string, value: string) => {
        assets.forEach(a => {
            if (a.displayId == id) {
                let newAssetInfo: assetInfo = { ...a }
                let newDetails = { ...newAssetInfo.details };
                if (newAssetInfo.assetCategory === "realestate") {
                    let realEstateDetails = { ...newDetails.realEstate };
                    newDetails.realEstate = {
                        occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
                        propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
                        rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
                        numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
                        ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
                        address: {
                            displayId: id,
                            streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                            streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                            streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                            streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                            unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                            unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                            city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                            province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                            postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                            country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
                        },
                        originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
                        originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
                        originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
                        propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
                        propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
                        heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
                        heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
                        maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
                        maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
                        lot: value,
                        block: realEstateDetails.block ? realEstateDetails.block : "",
                        plan: realEstateDetails.plan ? realEstateDetails.plan : "",
                        section: realEstateDetails.section ? realEstateDetails.section : "",
                        futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
                        hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
                        soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
                        rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
                        rentalIncomeText: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome.toString() : "",
                        manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
                        isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
                    }
                }
                newAssetInfo.details = newDetails;
                dispatch(setAssetInfo(newAssetInfo));
            }
        });
    };

    const setRealEstateBlock = (id: string, value: string) => {
        assets.forEach(a => {
            if (a.displayId == id) {
                let newAssetInfo: assetInfo = { ...a }
                let newDetails = { ...newAssetInfo.details };
                if (newAssetInfo.assetCategory === "realestate") {
                    let realEstateDetails = { ...newDetails.realEstate };
                    realEstateDetails.block = value;
                    newDetails.realEstate = {
                        occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
                        propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
                        rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
                        numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
                        ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
                        address: {
                            displayId: id,
                            streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                            streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                            streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                            streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                            unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                            unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                            city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                            province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                            postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                            country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
                        },
                        originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
                        originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
                        originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
                        propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
                        propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
                        heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
                        heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
                        maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
                        maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
                        lot: realEstateDetails.lot ? realEstateDetails.lot : "",
                        block: value,
                        plan: realEstateDetails.plan ? realEstateDetails.plan : "",
                        section: realEstateDetails.section ? realEstateDetails.section : "",
                        futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
                        hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
                        soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
                        rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
                        rentalIncomeText: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome.toString() : "",
                        manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
                        isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
                    }
                }
                newAssetInfo.details = newDetails;
                dispatch(setAssetInfo(newAssetInfo));
            }
        });
    };

    const setRealEstatePlan = (id: string, value: string) => {
        assets.forEach(a => {
            if (a.displayId == id) {
                let newAssetInfo: assetInfo = { ...a }
                let newDetails = { ...newAssetInfo.details };
                if (newAssetInfo.assetCategory === "realestate") {
                    let realEstateDetails = { ...newDetails.realEstate };
                    realEstateDetails.plan = value;
                    newDetails.realEstate = {
                        occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
                        propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
                        rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
                        numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
                        ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
                        address: {
                            displayId: id,
                            streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                            streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                            streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                            streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                            unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                            unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                            city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                            province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                            postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                            country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
                        },
                        originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
                        originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
                        originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
                        propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
                        propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
                        heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
                        heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
                        maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
                        maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
                        lot: realEstateDetails.lot ? realEstateDetails.lot : "",
                        block: realEstateDetails.block ? realEstateDetails.block : "",
                        plan: value,
                        section: realEstateDetails.section ? realEstateDetails.section : "",
                        futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
                        hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
                        soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
                        rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
                        rentalIncomeText: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome.toString() : "",
                        manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
                        isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
                    }
                }
                newAssetInfo.details = newDetails;
                dispatch(setAssetInfo(newAssetInfo));
            }
        });
    };

    const setRealEstateSection = (id: string, value: string) => {
        assets.forEach(a => {
            if (a.displayId == id) {
                let newAssetInfo: assetInfo = { ...a }
                let newDetails = { ...newAssetInfo.details };
                if (newAssetInfo.assetCategory === "realestate") {
                    let realEstateDetails = { ...newDetails.realEstate };
                    realEstateDetails.section = value;
                    newDetails.realEstate = {
                        occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
                        propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
                        rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
                        numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
                        ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
                        address: {
                            displayId: id,
                            streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                            streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                            streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                            streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                            unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                            unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                            city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                            province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                            postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                            country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
                        },
                        originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
                        originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
                        originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
                        propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
                        propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
                        heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
                        heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
                        maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
                        maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
                        lot: realEstateDetails.lot ? realEstateDetails.lot : "",
                        block: realEstateDetails.block ? realEstateDetails.block : "",
                        plan: realEstateDetails.plan ? realEstateDetails.plan : "",
                        section: value,
                        futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
                        hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
                        soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
                        rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
                        rentalIncomeText: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome.toString() : "",
                        manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
                        isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
                    }
                }
                newAssetInfo.details = newDetails;
                dispatch(setAssetInfo(newAssetInfo));
            }
        });
    };

    const setPurposeOfProperty = (id: string, purpose: string) => {
        assets.forEach(a => {
            if (a.displayId == id) {
                let newAssetInfo: assetInfo = { ...a }
                let newDetails = { ...newAssetInfo.details };
                if (newAssetInfo.assetCategory === "realestate") {
                    let realEstateDetails = { ...newDetails.realEstate };
                    realEstateDetails.occupancyType = purpose;
                    newDetails.realEstate = newDetails.realEstate = {
                        occupancyType: purpose,
                        propertyType: purpose === "vacantland" ? undefined : realEstateDetails.propertyType ? realEstateDetails.propertyType : "",
                        rentalComponent: purpose === "primary" ? realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "" : "",
                        numOfProperties: purpose === "vacantland" ? "" : realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
                        rentalIncome: purpose === "rental" ? realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0 : 0,
                        rentalIncomeText: purpose === "rental" ? realEstateDetails.rentalIncomeText ? realEstateDetails.rentalIncomeText : "" : "",
                        ownershipPercentage: purpose === "rental" ? realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "" : realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
                        address: {
                            displayId: id,
                            streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                            streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                            streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                            streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                            unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                            unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                            city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                            province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                            postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                            country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
                        },
                        originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
                        originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
                        originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
                        propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
                        propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
                        heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
                        heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
                        maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
                        maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
                        lot: realEstateDetails.lot ? realEstateDetails.lot : "",
                        block: realEstateDetails.block ? realEstateDetails.block : "",
                        plan: realEstateDetails.plan ? realEstateDetails.plan : "",
                        section: realEstateDetails.section ? realEstateDetails.section : "",
                        futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
                        hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
                        soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
                        manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
                        isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
                    }
                }
                newAssetInfo.details = newDetails;
                dispatch(setAssetInfo(newAssetInfo));
            }
        });
        if (assetErrors[parseInt(id) - 1]["useOfProperty"] != undefined && purpose.trim() != "") {
            let newAssetErrors = [...assetErrors];
            delete newAssetErrors[parseInt(id) - 1]["useOfProperty"];
            setAssetErrors(newAssetErrors);
        }
    }

    const setOriginalPurchasePrice = (id: string, newAmount: string) => {
        assets.forEach(a => {
            if (a.displayId == id) {
                let newAssetInfo: assetInfo = { ...a }
                let newDetails = { ...newAssetInfo.details };
                if (newAssetInfo.assetCategory === "realestate") {
                    let realEstateDetails = { ...newDetails.realEstate };
                    newDetails.realEstate = {
                        occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
                        propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
                        rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
                        numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
                        ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
                        address: {
                            displayId: id,
                            streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                            streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                            streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                            streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                            unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                            unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                            city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                            province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                            postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                            country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
                        },
                        originalPurchasePrice: +newAmount,
                        originalPurchasePriceText: newAmount,
                        originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
                        propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
                        propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
                        heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
                        heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
                        maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
                        maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
                        lot: realEstateDetails.lot ? realEstateDetails.lot : "",
                        block: realEstateDetails.block ? realEstateDetails.block : "",
                        plan: realEstateDetails.plan ? realEstateDetails.plan : "",
                        section: realEstateDetails.section ? realEstateDetails.section : "",
                        futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
                        hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
                        soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
                        rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
                        rentalIncomeText: realEstateDetails.rentalIncomeText ? realEstateDetails.rentalIncomeText : "",
                        manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
                        isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
                    };
                }
                newAssetInfo.details = newDetails;
                dispatch(setAssetInfo(newAssetInfo));
            }
        });
    }

    const setOriginalPurchaseDate = (id: string, newDate: string) => {
        assets.forEach(a => {
            if (a.displayId == id) {
                let newAssetInfo: assetInfo = { ...a }
                let newDetails = { ...newAssetInfo.details };
                if (newAssetInfo.assetCategory === "realestate") {
                    let realEstateDetails = { ...newDetails.realEstate };
                    newDetails.realEstate = {
                        occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
                        propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
                        rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
                        numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
                        ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
                        address: {
                            displayId: id,
                            streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                            streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                            streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                            streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                            unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                            unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                            city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                            province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                            postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                            country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
                        },
                        originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
                        originalPurchasePriceText: realEstateDetails.originalPurchasePriceText ? realEstateDetails.originalPurchasePriceText : "",
                        originalPurchaseDate: newDate,
                        propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
                        propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
                        heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
                        heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
                        maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
                        maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
                        lot: realEstateDetails.lot ? realEstateDetails.lot : "",
                        block: realEstateDetails.block ? realEstateDetails.block : "",
                        plan: realEstateDetails.plan ? realEstateDetails.plan : "",
                        section: realEstateDetails.section ? realEstateDetails.section : "",
                        futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
                        hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
                        soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
                        rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
                        rentalIncomeText: realEstateDetails.rentalIncomeText ? realEstateDetails.rentalIncomeText : "",
                        manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
                        isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
                    };
                }
                newAssetInfo.details = newDetails;
                dispatch(setAssetInfo(newAssetInfo));
            }
        });
    }

    const setRealEstateFutureUsage = (id: string, purpose: string) => {
        assets.forEach(a => {
            if (a.displayId == id) {
                let newAssetInfo: assetInfo = { ...a }
                let newDetails = { ...newAssetInfo.details };
                if (newAssetInfo.assetCategory === "realestate") {
                    let realEstateDetails = { ...newDetails.realEstate };
                    newDetails.realEstate = newDetails.realEstate = {
                        occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
                        propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
                        rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
                        numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
                        ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
                        address: {
                            displayId: id,
                            streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                            streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                            streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                            streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                            unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                            unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                            city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                            province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                            postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                            country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
                        },
                        originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
                        originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
                        originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
                        propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
                        propertyTaxText: realEstateDetails.propertyTaxText ? realEstateDetails.propertyTaxText : "",
                        heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
                        heatingCostText: realEstateDetails.heatingCostText ? realEstateDetails.heatingCostText : "",
                        maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
                        maintenanceFeeText: realEstateDetails.maintenanceFeeText ? realEstateDetails.maintenanceFeeText : "",
                        lot: realEstateDetails.lot ? realEstateDetails.lot : "",
                        block: realEstateDetails.block ? realEstateDetails.block : "",
                        plan: realEstateDetails.plan ? realEstateDetails.plan : "",
                        section: realEstateDetails.section ? realEstateDetails.section : "",
                        futureUsage: purpose,
                        hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
                        soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
                        rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
                        rentalIncomeText: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome.toString() : "",
                        manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
                        isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
                    }
                }
                newAssetInfo.details = newDetails;
                dispatch(setAssetInfo(newAssetInfo));
            }
        });
        if (assetErrors[parseInt(id) - 1]["futureUsage"] != undefined && purpose.trim() != "") {
            let newAssetErrors = [...assetErrors];
            delete newAssetErrors[parseInt(id) - 1]["futureUsage"];
            setAssetErrors(newAssetErrors);
        }
    }

    const setPropertySold = (id: string, hasPropertyBeenSold: string) => {
        assets.forEach(a => {
            if (a.displayId == id) {
                let newAssetInfo: assetInfo = { ...a }
                let newDetails = { ...newAssetInfo.details };
                if (newAssetInfo.assetCategory === "realestate") {
                    let realEstateDetails = { ...newDetails.realEstate };
                    realEstateDetails.hasPropertyBeenSold = hasPropertyBeenSold;
                    realEstateDetails.soldDate = hasPropertyBeenSold === "yes" ? realEstateDetails.soldDate : "";
                    newDetails.realEstate = newDetails.realEstate = {
                        occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
                        propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
                        rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
                        numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
                        ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
                        address: {
                            displayId: id,
                            streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                            streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                            streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                            streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                            unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                            unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                            city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                            province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                            postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                            country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
                        },
                        originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
                        originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
                        originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
                        propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
                        propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
                        heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
                        heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
                        maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
                        maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
                        lot: realEstateDetails.lot ? realEstateDetails.lot : "",
                        block: realEstateDetails.block ? realEstateDetails.block : "",
                        plan: realEstateDetails.plan ? realEstateDetails.plan : "",
                        section: realEstateDetails.section ? realEstateDetails.section : "",
                        futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
                        hasPropertyBeenSold: hasPropertyBeenSold,
                        soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
                        rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
                        rentalIncomeText: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome.toString() : "",
                        manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
                        isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
                    }
                }
                newAssetInfo.details = newDetails;
                dispatch(setAssetInfo(newAssetInfo));
            }
        });
    }

    const setPropertySoldDate = (id: string, soldDate: string) => {
        assets.forEach(a => {
            if (a.displayId == id) {
                let newAssetInfo: assetInfo = { ...a }
                let newDetails = { ...newAssetInfo.details };
                if (newAssetInfo.assetCategory === "realestate") {
                    let realEstateDetails = { ...newDetails.realEstate };
                    realEstateDetails.soldDate = soldDate;
                    newDetails.realEstate = newDetails.realEstate = {
                        occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
                        propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
                        rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
                        numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
                        ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
                        address: {
                            displayId: id,
                            streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                            streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                            streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                            streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                            unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                            unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                            city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                            province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                            postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                            country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
                        },
                        originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
                        originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
                        originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
                        propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
                        propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
                        heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
                        heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
                        maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
                        maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
                        lot: realEstateDetails.lot ? realEstateDetails.lot : "",
                        block: realEstateDetails.block ? realEstateDetails.block : "",
                        plan: realEstateDetails.plan ? realEstateDetails.plan : "",
                        section: realEstateDetails.section ? realEstateDetails.section : "",
                        futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
                        hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
                        soldDate: soldDate,
                        rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
                        rentalIncomeText: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome.toString() : "",
                        manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
                        isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
                    }
                }
                newAssetInfo.details = newDetails;
                dispatch(setAssetInfo(newAssetInfo));
            }
        });
    }

    const setPropertyType = (id: string, propertyType: string) => {
        assets.forEach(a => {
            if (a.displayId == id) {
                let newAssetInfo: assetInfo = { ...a }
                let newDetails = { ...newAssetInfo.details };
                if (newAssetInfo.assetCategory === "realestate") {
                    let realEstateDetails = { ...newDetails.realEstate };
                    realEstateDetails.propertyType = propertyType;
                    newDetails.realEstate = newDetails.realEstate = {
                        occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
                        propertyType: propertyType,
                        rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
                        numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
                        ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
                        address: {
                            displayId: id,
                            streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                            streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                            streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                            streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                            unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                            unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                            city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                            province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                            postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                            country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
                        },
                        originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
                        originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
                        originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
                        propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
                        propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
                        heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
                        heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
                        maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
                        maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
                        lot: realEstateDetails.lot ? realEstateDetails.lot : "",
                        block: realEstateDetails.block ? realEstateDetails.block : "",
                        plan: realEstateDetails.plan ? realEstateDetails.plan : "",
                        section: realEstateDetails.section ? realEstateDetails.section : "",
                        futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
                        hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
                        soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
                        rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
                        rentalIncomeText: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome.toString() : "",
                        manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
                        isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
                    }
                }
                newAssetInfo.details = newDetails;
                dispatch(setAssetInfo(newAssetInfo));
            }
        });
        if (assetErrors[parseInt(id) - 1]["propertyType"] != undefined && propertyType.trim() != "") {
            let newAssetErrors = [...assetErrors];
            delete newAssetErrors[parseInt(id) - 1]["propertyType"];
            setAssetErrors(newAssetErrors);
        }
    }

    const setRentalComponent = (id: string, rentalComponent: string) => {
        assets.forEach(a => {
            if (a.displayId == id) {
                let newAssetInfo: assetInfo = { ...a }
                let newDetails = { ...newAssetInfo.details };
                if (newAssetInfo.assetCategory === "realestate") {
                    let realEstateDetails = { ...newDetails.realEstate };
                    realEstateDetails.rentalComponent = rentalComponent;
                    if (rentalComponent === "no") {
                        realEstateDetails.rentalIncome = 0;
                        realEstateDetails.rentalIncomeText = "";
                    }
                    newDetails.realEstate = newDetails.realEstate = {
                        occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
                        propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
                        rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
                        numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
                        ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
                        address: {
                            displayId: id,
                            streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                            streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                            streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                            streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                            unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                            unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                            city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                            province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                            postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                            country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
                        },
                        originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
                        originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
                        originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
                        propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
                        propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
                        heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
                        heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
                        maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
                        maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
                        lot: realEstateDetails.lot ? realEstateDetails.lot : "",
                        block: realEstateDetails.block ? realEstateDetails.block : "",
                        plan: realEstateDetails.plan ? realEstateDetails.plan : "",
                        section: realEstateDetails.section ? realEstateDetails.section : "",
                        futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
                        hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
                        soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
                        rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
                        rentalIncomeText: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome.toString() : "",
                        manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
                        isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
                    }
                }
                newAssetInfo.details = newDetails;
                dispatch(setAssetInfo(newAssetInfo));
            }
        });
    }

    const setRentalIncomeValue = (id: string, newAmount: string) => {
        const idx = assets.findIndex(a => a.displayId === id);
        if (idx === -1) return;
        const current = assets[idx];
        if (current.assetCategory !== "realestate") return;
        const clean = (newAmount ?? "").replace(/[^0-9.]/g, "");
        const nextText = clean;
        const nextValue = clean ? +clean : 0;
        const currText = current.details?.realEstate?.rentalIncomeText ?? "";
        const currValue = current.details?.realEstate?.rentalIncome ?? 0;
        if (currText === nextText && currValue === nextValue) return;
        const newDetails = { ...current.details };
        const realEstateDetails = { ...newDetails.realEstate };
        newDetails.realEstate = {
            occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
            propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
            rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
            numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
            ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
            address: {
                displayId: id,
                streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
            },
            originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
            originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
            originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
            propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
            propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
            heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
            heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
            maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
            maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
            lot: realEstateDetails.lot ? realEstateDetails.lot : "",
            block: realEstateDetails.block ? realEstateDetails.block : "",
            plan: realEstateDetails.plan ? realEstateDetails.plan : "",
            section: realEstateDetails.section ? realEstateDetails.section : "",
            futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
            hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
            soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
            rentalIncome: nextValue,
            rentalIncomeText: nextText,
            manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
            isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
        };
        const newAssetInfo: assetInfo = { ...current, details: newDetails };
        dispatch(setAssetInfo(newAssetInfo));
    }

    const setNumOfProperties = (id: string, numOfProperties: string) => {
        const idx = assets.findIndex(a => a.displayId === id);
        if (idx === -1) return;
        const current = assets[idx];
        if (current.assetCategory !== "realestate") return;
        const curr = current.details?.realEstate?.numOfProperties ?? "";
        const next = numOfProperties ?? "";
        if (curr === next) return;
        const newDetails = { ...current.details };
        const realEstateDetails = { ...newDetails.realEstate };
        newDetails.realEstate = {
            occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
            propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
            rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
            ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
            numOfProperties: next,
            address: {
                displayId: id,
                streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
            },
            originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
            originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
            originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
            propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
            propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
            heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
            heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
            maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
            maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
            lot: realEstateDetails.lot ? realEstateDetails.lot : "",
            block: realEstateDetails.block ? realEstateDetails.block : "",
            plan: realEstateDetails.plan ? realEstateDetails.plan : "",
            section: realEstateDetails.section ? realEstateDetails.section : "",
            futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
            hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
            soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
            rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
            rentalIncomeText: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome.toString() : "",
            manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
            isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
        };
        const newAssetInfo: assetInfo = { ...current, details: newDetails };
        dispatch(setAssetInfo(newAssetInfo));
    }

    const setPropertyTax = (id: string, newAmount: string) => {
        assets.forEach(a => {
            if (a.displayId == id) {
                let newAssetInfo: assetInfo = { ...a }
                let newDetails = { ...newAssetInfo.details };
                if (newAssetInfo.assetCategory === "realestate") {
                    let realEstateDetails = { ...newDetails.realEstate };
                    newDetails.realEstate = {
                        occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
                        propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
                        rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
                        numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
                        ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
                        address: {
                            displayId: id,
                            streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                            streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                            streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                            streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                            unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                            unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                            city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                            province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                            postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                            country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
                        },
                        originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
                        originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
                        originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
                        propertyTax: +newAmount,
                        propertyTaxText: newAmount,
                        heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
                        heatingCostText: realEstateDetails.heatingCostText ? realEstateDetails.heatingCostText : "",
                        maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
                        maintenanceFeeText: realEstateDetails.maintenanceFeeText ? realEstateDetails.maintenanceFeeText : "",
                        lot: realEstateDetails.lot ? realEstateDetails.lot : "",
                        block: realEstateDetails.block ? realEstateDetails.block : "",
                        plan: realEstateDetails.plan ? realEstateDetails.plan : "",
                        section: realEstateDetails.section ? realEstateDetails.section : "",
                        futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
                        hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
                        soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
                        rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
                        rentalIncomeText: realEstateDetails.rentalIncomeText ? realEstateDetails.rentalIncomeText : "",
                        manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
                        isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
                    };
                }
                newAssetInfo.details = newDetails;
                dispatch(setAssetInfo(newAssetInfo));
            }
        });
        if (assetErrors[parseInt(id) - 1]["propertyTax"] != undefined && newAmount.trim() != "") {
            let newAssetErrors = [...assetErrors];
            delete newAssetErrors[parseInt(id) - 1]["propertyTax"];
            setAssetErrors(newAssetErrors);
        }
    }

    const setHeatingCost = (id: string, newAmount: string) => {
        assets.forEach(a => {
            if (a.displayId == id) {
                let newAssetInfo: assetInfo = { ...a }
                let newDetails = { ...newAssetInfo.details };
                if (newAssetInfo.assetCategory === "realestate") {
                    let realEstateDetails = { ...newDetails.realEstate };
                    newDetails.realEstate = {
                        occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
                        propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
                        rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
                        numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
                        ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
                        address: {
                            displayId: id,
                            streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                            streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                            streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                            streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                            unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                            unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                            city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                            province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                            postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                            country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
                        },
                        originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
                        originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
                        originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
                        propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
                        propertyTaxText: realEstateDetails.propertyTaxText ? realEstateDetails.propertyTaxText : "",
                        heatingCost: +newAmount,
                        heatingCostText: newAmount,
                        maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
                        maintenanceFeeText: realEstateDetails.maintenanceFeeText ? realEstateDetails.maintenanceFeeText : "",
                        lot: realEstateDetails.lot ? realEstateDetails.lot : "",
                        block: realEstateDetails.block ? realEstateDetails.block : "",
                        plan: realEstateDetails.plan ? realEstateDetails.plan : "",
                        section: realEstateDetails.section ? realEstateDetails.section : "",
                        futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
                        hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
                        soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
                        rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
                        rentalIncomeText: realEstateDetails.rentalIncomeText ? realEstateDetails.rentalIncomeText : "",
                        manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
                        isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
                    };
                }
                newAssetInfo.details = newDetails;
                dispatch(setAssetInfo(newAssetInfo));
            }
        });
        if (assetErrors[parseInt(id) - 1]["heatingCost"] != undefined && newAmount.trim() != "") {
            let newAssetErrors = [...assetErrors];
            delete newAssetErrors[parseInt(id) - 1]["heatingCost"];
            setAssetErrors(newAssetErrors);
        }
    }

    const setMaintenanceCost = (id: string, newAmount: string) => {
        assets.forEach(a => {
            if (a.displayId == id) {
                let newAssetInfo: assetInfo = { ...a }
                let newDetails = { ...newAssetInfo.details };
                if (newAssetInfo.assetCategory === "realestate") {
                    let realEstateDetails = { ...newDetails.realEstate };
                    newDetails.realEstate = {
                        occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
                        propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
                        rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
                        numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
                        ownershipPercentage: realEstateDetails.ownershipPercentage ? realEstateDetails.ownershipPercentage : "",
                        address: {
                            displayId: id,
                            streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                            streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                            streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                            streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                            unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                            unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                            city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                            province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                            postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                            country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
                        },
                        originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
                        originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
                        originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
                        propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
                        propertyTaxText: realEstateDetails.propertyTaxText ? realEstateDetails.propertyTaxText : "",
                        heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
                        heatingCostText: realEstateDetails.heatingCostText ? realEstateDetails.heatingCostText : "",
                        maintenanceFee: +newAmount,
                        maintenanceFeeText: newAmount,
                        lot: realEstateDetails.lot ? realEstateDetails.lot : "",
                        block: realEstateDetails.block ? realEstateDetails.block : "",
                        plan: realEstateDetails.plan ? realEstateDetails.plan : "",
                        section: realEstateDetails.section ? realEstateDetails.section : "",
                        futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
                        hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
                        soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
                        rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
                        rentalIncomeText: realEstateDetails.rentalIncomeText ? realEstateDetails.rentalIncomeText : "",
                        manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
                        isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
                    };
                }
                newAssetInfo.details = newDetails;
                dispatch(setAssetInfo(newAssetInfo));
            }
        });
        if (assetErrors[parseInt(id) - 1]["maintenanceFee"] != undefined && newAmount.trim() != "") {
            let newAssetErrors = [...assetErrors];
            delete newAssetErrors[parseInt(id) - 1]["maintenanceFee"];
            setAssetErrors(newAssetErrors);
        }
    }

    const setOwnershipPercentage = (id: string, ownershipPercentage: string) => {
        const idx = assets.findIndex(a => a.displayId === id);
        if (idx === -1) return;
        const current = assets[idx];
        if (current.assetCategory !== "realestate") return;
        const curr = current.details?.realEstate?.ownershipPercentage ?? "";
        const next = ownershipPercentage ?? "";
        if (curr === next) return;
        const newDetails = { ...current.details };
        const realEstateDetails = { ...newDetails.realEstate };
        newDetails.realEstate = {
            occupancyType: realEstateDetails.occupancyType ? realEstateDetails.occupancyType : "",
            propertyType: realEstateDetails.propertyType ? realEstateDetails.propertyType : undefined,
            rentalComponent: realEstateDetails.rentalComponent ? realEstateDetails.rentalComponent : "",
            numOfProperties: realEstateDetails.numOfProperties ? realEstateDetails.numOfProperties : "",
            ownershipPercentage: next,
            address: {
                displayId: id,
                streetNumber: realEstateDetails.address?.streetNumber ? realEstateDetails.address.streetNumber : "",
                streetName: realEstateDetails.address?.streetName ? realEstateDetails.address.streetName : "",
                streetType: realEstateDetails.address?.streetType ? realEstateDetails.address.streetType : "",
                streetDirection: realEstateDetails.address?.streetDirection ? realEstateDetails.address.streetDirection : "",
                unitType: realEstateDetails.address?.unitType ? realEstateDetails.address.unitType : "",
                unitNumber: realEstateDetails.address?.unitNumber ? realEstateDetails.address.unitNumber : "",
                city: realEstateDetails.address?.city ? realEstateDetails.address.city : "",
                province: realEstateDetails.address?.province ? realEstateDetails.address.province : "",
                postalCode: realEstateDetails.address?.postalCode ? realEstateDetails.address.postalCode : "",
                country: realEstateDetails.address?.country ? realEstateDetails.address.country : ""
            },
            originalPurchasePrice: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice : 0,
            originalPurchasePriceText: realEstateDetails.originalPurchasePrice ? realEstateDetails.originalPurchasePrice.toString() : "",
            originalPurchaseDate: realEstateDetails.originalPurchaseDate ? realEstateDetails.originalPurchaseDate : "",
            propertyTax: realEstateDetails.propertyTax ? realEstateDetails.propertyTax : 0,
            propertyTaxText: realEstateDetails.propertyTax ? realEstateDetails.propertyTax.toString() : "",
            heatingCost: realEstateDetails.heatingCost ? realEstateDetails.heatingCost : 0,
            heatingCostText: realEstateDetails.heatingCost ? realEstateDetails.heatingCost.toString() : "",
            maintenanceFee: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee : 0,
            maintenanceFeeText: realEstateDetails.maintenanceFee ? realEstateDetails.maintenanceFee.toString() : "",
            lot: realEstateDetails.lot ? realEstateDetails.lot : "",
            block: realEstateDetails.block ? realEstateDetails.block : "",
            plan: realEstateDetails.plan ? realEstateDetails.plan : "",
            section: realEstateDetails.section ? realEstateDetails.section : "",
            futureUsage: realEstateDetails.futureUsage ? realEstateDetails.futureUsage : "",
            hasPropertyBeenSold: realEstateDetails.hasPropertyBeenSold ? realEstateDetails.hasPropertyBeenSold : "",
            soldDate: realEstateDetails.soldDate ? realEstateDetails.soldDate : "",
            rentalIncome: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome : 0,
            rentalIncomeText: realEstateDetails.rentalIncome ? realEstateDetails.rentalIncome.toString() : "",
            manualAddressEntered: realEstateDetails.manualAddressEntered ? realEstateDetails.manualAddressEntered : false,
            isCurrentResidence: realEstateDetails.isCurrentResidence ? realEstateDetails.isCurrentResidence : false
        };
        const newAssetInfo: assetInfo = { ...current, details: newDetails };
        dispatch(setAssetInfo(newAssetInfo));
    }

    /*
    Update operations for Life Insurance Asset Types
    */

    const setCashSurrenderValue = (id: string, newAmount: string) => {
        const idx = assets.findIndex(a => a.displayId === id);
        if (idx === -1) return;
        const current = assets[idx];
        if (current.assetCategory !== "lifeinsurance") return;
        const clean = (newAmount ?? "").replace(/[^0-9.]/g, "");
        const nextText = clean;
        const nextValue = clean ? +clean : 0;
        const currText = current.details?.lifeInsurance?.cashSurrenderValueText ?? "";
        const currValue = current.details?.lifeInsurance?.cashSurrenderValue ?? 0;
        if (currText === nextText && currValue === nextValue) return;
        const newDetails = { ...current.details };
        newDetails.lifeInsurance = {
            cashSurrenderValue: nextValue,
            cashSurrenderValueText: nextText
        };
        const newAssetInfo: assetInfo = { ...current, details: newDetails };
        dispatch(setAssetInfo(newAssetInfo));

        if (assetErrors[parseInt(id) - 1]["cashSurrenderValue"] != undefined && newAmount.trim() != "") {
            let newAssetErrors = [...assetErrors];
            delete newAssetErrors[parseInt(id) - 1]["cashSurrenderValue"];
            setAssetErrors(newAssetErrors);
        }
    }

    const searchVehicleModels = (year: string, make: string) => {
        setFetchingModels(true);
        client.apiDataVehiclemakes(year, make).then((response: VehicleModel[]) => {
            if (response && response.length > 0) {
                setVehicleModels(
                    response
                        .map((model: VehicleModel) => ({
                            code: model.model_Name,
                            name: model.model_Name
                        }))
                        .sort((a, b) => a.name != undefined && b.name != undefined ? a.name.localeCompare(b.name) : 0)
                );
            } else {
                setVehicleModels([]);
            }
            setFetchingModels(false);
        }).catch((error) => {
            console.error("Error fetching vehicle models:", error);
            setVehicleModels([]);
            setFetchingModels(false);
        });
    }

    const confirmDeletion = (id: string) => {
        confirmDialog({
            message: <p style={{ marginLeft: -14 }}>Are you sure you would like to remove this asset?</p>,
            header: <h2 className="flex"><img src="assets/error-icon.svg" style={{ width: 24, marginRight: 12 }} />Remove Asset?</h2>,
            defaultFocus: 'reject',
            acceptClassName: style.confirm,
            acceptLabel: "Remove Asset",
            rejectClassName: style.cancel,
            rejectLabel: "Cancel",
            accept: () => {
                //Remove Asset
                dispatch(removeAsset(id));
            },
            draggable: false,
            closable: false
        });
    };

    const assetsList = () => {
        return (
            <AnimatePresence>
                {assets.map((position: any) => (
                    <motion.div
                        key={position.displayId}
                        className={clsx("content-visibility-auto")}
                        initial={{ y: -100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ type: "linear" }}
                    >
                        <div className="flex flex-row justify-between mt-5">
                            <label htmlFor="" className="cursor-pointer"
                                style={{ alignContent: "center", fontSize: 20, textWrap: "nowrap" }}
                                onClick={() => { toggleAssetVisibility(position.displayId) }}>Source {position.displayId}</label>
                            <div className="cursor-pointer" style={{ width: "100%" }} onClick={() => { toggleAssetVisibility(position.displayId) }}></div>
                            <div className="flex flex-row">
                                {position.origin != "internal" && !position.details?.realEstate?.isCurrentResidence && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (position.assetCategory != "") {
                                                confirmDeletion(position.displayId);
                                            } else {
                                                dispatch(removeAsset(position.displayId));
                                            }
                                        }}
                                        className="secondary sm:w-28 h-11 px-6 ml-3"
                                        style={{ color: 'var(--brand-blue)', background: "transparent", width: 125, order: 0 }}>
                                        <img
                                            className="mr-3"
                                            src="assets/remove-item.svg"
                                            alt="tooltip"
                                            width={20}
                                            height={20}
                                        />
                                        Remove
                                    </button>
                                )}
                                <img
                                    onClick={() => { toggleAssetVisibility(position.displayId) }}
                                    src={position.visible ? "assets/chevron-down.svg" : "assets/chevron-up.svg"}
                                    className={clsx(
                                        "w-5 h-6 top-6 ml-3 mr-3", position.origin === "internal" ? "cursor-pointer" : ""
                                    )}
                                    alt="error"
                                />
                            </div>
                        </div>

                        {position.visible &&
                            <>
                                <div className="flex md:flex-row flex-col">
                                    <div className={clsx(style.twoColFirst, "mt-5")}>
                                        <label htmlFor={"assetCategory" + position.displayId}>Down payment source</label>
                                        <div className="flex relative">
                                            <span>
                                                <img
                                                    src="assets/error-icon.svg"
                                                    className={clsx(
                                                        assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["assetCategory"] != undefined
                                                            ? ""
                                                            : "hidden",
                                                        "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                    )}
                                                    alt="error"
                                                    height={5}
                                                    width={5}
                                                />
                                            </span>
                                            {position.origin === "internal" &&
                                                <h1 className="internal-asset">{position.assetCategory == "nonregistered" ? "Non-Registered Asset" : "Registered Asset"}</h1>
                                            }
                                            {position.origin !== "internal" &&
                                                <select
                                                    id={"assetCategory" + position.displayId}
                                                    onChange={(e) => {
                                                        setAssetCategory(position.displayId, e.target.value);
                                                    }}
                                                    value={position.assetCategory}
                                                    disabled={position.origin === "internal" || position.details?.realEstate?.isCurrentResidence}
                                                    className={clsx(
                                                        assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["assetCategory"] != undefined
                                                            ? "error"
                                                            : "",
                                                        "mt-2 w:[220px] md:w-[305px]"
                                                    )}
                                                    style={{ alignItems: 'center' }}
                                                >
                                                    <option hidden value="">
                                                        Choose...
                                                    </option>
                                                    <optgroup label="ASSETS">
                                                        <option value="nonregistered">Non-Registered Assets</option>
                                                        <option value="registered">Registered Assets</option>
                                                        <option value="vehicle">Vehicles</option>
                                                        <option value="realestate">Real Estate</option>
                                                        <option value="lifeinsurance">Life Insurance</option>
                                                        <option value="other">Other Assets</option>
                                                    </optgroup>
                                                    <optgroup label="ADDITIONAL DOWN PAYMENT SOURCES">
                                                        <option value="gift">Gift</option>
                                                        <option value="depositonpurchase">Deposit on Purchase</option>
                                                        <option value="builderdeposit">Builder Deposits</option>
                                                    </optgroup>
                                                </select>
                                            }
                                        </div>
                                        {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["assetCategory"] != undefined && (
                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                {assetErrors[position.displayId - 1]["assetCategory"]}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <hr className="mt-5" />

                                {(position?.assetCategory === "nonregistered" || position?.assetCategory === "registered") &&
                                    <>
                                        <div className="flex md:flex-row flex-col">
                                            <div className={clsx(style.twoColFirst, "mt-5")}>
                                                <label htmlFor="assetType">Asset Type</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["assetType"] != undefined
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <select
                                                        id="assetType"
                                                        onChange={(e) => {
                                                            setAssetType(position.displayId, e.target.value)
                                                        }}
                                                        value={position.assetType}
                                                        disabled={position.origin === "internal"}
                                                        className={clsx(
                                                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["assetType"] != undefined
                                                                ? "error"
                                                                : "",
                                                            "mt-2 w:[220px] md:w-[305px]"
                                                        )}
                                                        style={{ alignItems: 'center' }}
                                                    >
                                                        <option hidden value="">
                                                            Choose...
                                                        </option>
                                                        {position?.assetCategory === "nonregistered" &&
                                                            <>
                                                                <option value="chequingaccount">Chequing Account</option>
                                                                <option value="savingsaccount">Savings Account</option>
                                                                <option value="termgic">Term/GIC</option>
                                                                {position?.origin == "internal" && <option value="term_gic">Term/GIC</option>}
                                                                <option value="sharesstocks">Shares/Stocks</option>
                                                                <option value="mutualfunds">Mutual Funds</option>
                                                            </>
                                                        }
                                                        {position?.assetCategory === "registered" &&
                                                            <>
                                                                <option value="rrsp">RRSP</option>
                                                                <option value="resp">RESP</option>
                                                                <option value="rrifliflira">RRIF/LIF/LIRA</option>
                                                                <option value="tfsa">TFSA</option>
                                                                <option value="other">Other</option>
                                                                <option value="fhsa">FHSA</option>
                                                            </>
                                                        }
                                                    </select>
                                                </div>
                                                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["assetType"] != undefined && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {assetErrors[position.displayId - 1]["assetType"]}
                                                    </span>
                                                )}
                                            </div>
                                            {assetValue(position, false, 2)}
                                        </div>
                                        <div className="flex md:flex-row flex-col">
                                            {position?.origin !== "internal" &&
                                                <div className={clsx(style.twoColFirst, "mt-5")}>
                                                    <label htmlFor="fiName">Financial Institution</label>
                                                    <div className="flex relative">
                                                        <span>
                                                            <img
                                                                src="assets/error-icon.svg"
                                                                className={clsx(
                                                                    assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["fi"] != undefined
                                                                        ? ""
                                                                        : "hidden",
                                                                    "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                )}
                                                                alt="error"
                                                                height={5}
                                                                width={5}
                                                            />
                                                        </span>
                                                        <select
                                                            id="fiName"
                                                            onChange={(e) => {
                                                                setFinancialInstitution(position.displayId, e.target.value)
                                                            }}
                                                            value={position.assetCategory == "nonregistered" ? position.details.nonRegistered?.financialInstitution
                                                                : position.assetCategory == "registered" ? position.details.registered?.financialInstitution : ""}
                                                            disabled={position.origin === "internal"}
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["fiName"] != undefined
                                                                    ? "error"
                                                                    : "",
                                                                "mt-2 w:[220px] md:w-[305px]"
                                                            )}
                                                            style={{ alignItems: 'center' }}
                                                        >
                                                            <option hidden value="">
                                                                Choose...
                                                            </option>
                                                            {sessionData.financialInstitutions.map((fi) => (
                                                                <option key={fi.id} value={fi.name}>
                                                                    {fi.name}
                                                                </option>
                                                            ))}
                                                            <option value="other">Other (Please Specify)</option>
                                                        </select>
                                                    </div>
                                                    {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["fiName"] != undefined && (
                                                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                            {assetErrors[position.displayId - 1]["fiName"]}
                                                        </span>
                                                    )}
                                                </div>
                                            }
                                            {(position.details?.nonRegistered?.financialInstitution == "other" ||
                                                position.details?.registered?.financialInstitution == "other") &&
                                                <div className="mt-5">
                                                    <label htmlFor={position.displayId + "fiOtherName"}>Financial Institution Name</label>
                                                    <div className="flex relative">
                                                        <span>
                                                            <img
                                                                src="assets/error-icon.svg"
                                                                className={clsx(
                                                                    assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["fiOtherName"] != undefined
                                                                        ? ""
                                                                        : "hidden",
                                                                    "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                )}
                                                                alt="error"
                                                                height={5}
                                                                width={5}
                                                            />
                                                        </span>
                                                        <input
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["fiOtherName"] != undefined
                                                                    ? "error"
                                                                    : "",
                                                                "mt-2 w:[220px] md:w-[305px]"
                                                            )}
                                                            type="text"
                                                            id={"otherFi" + position.displayId}
                                                            onChange={(e) => {
                                                                setOtherFinancialInstitution(position.displayId, e.target.value)
                                                            }}
                                                            value={position.assetCategory == "nonregistered" ? position.details.nonRegistered?.otherFinancialInstitution
                                                                : position.assetCategory == "registered" ? position.details.registered?.otherFinancialInstitution : ""}
                                                            disabled={position.origin === "internal"}
                                                            maxLength={30}
                                                        />
                                                    </div>
                                                    {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["fiOtherName"] != undefined && (
                                                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                            {assetErrors[position.displayId - 1]["fiOtherName"]}
                                                        </span>
                                                    )}
                                                </div>
                                            }
                                        </div>
                                        <div className="flex md:flex-row flex-col">
                                            {assetOwnership(position, true)}
                                            {assetUtilization(position, false)}
                                        </div>
                                    </>
                                }

                                {position?.assetCategory === "vehicle" &&
                                    <>
                                        <div className="flex md:flex-row flex-col">
                                            <div className={clsx(style.twoColFirst, "mt-5")}>
                                                <label htmlFor="assetType">Vehicle Type</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["assetType"] != undefined
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <select
                                                        id="assetType"
                                                        onChange={(e) => {
                                                            setAssetType(position.displayId, e.target.value)
                                                        }}
                                                        value={position.assetType}
                                                        className={clsx(
                                                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["assetType"] != undefined
                                                                ? "error"
                                                                : "",
                                                            "mt-2 w:[220px] md:w-[305px]"
                                                        )}
                                                        style={{ alignItems: 'center' }}
                                                    >
                                                        <option hidden value="">
                                                            Choose...
                                                        </option>
                                                        <option value="car">Car</option>
                                                        <option value="truck">Truck</option>
                                                        <option value="motorcycle">Motorcycle</option>
                                                        <option value="boat">Boat</option>
                                                        <option value="atv">ATV</option>
                                                        <option value="other">Other</option>
                                                    </select>
                                                </div>
                                                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["assetType"] != undefined && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {assetErrors[position.displayId - 1]["assetType"]}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex md:flex-row flex-col mt-5">
                                            <div className={style.threeColFirst}>
                                                <label htmlFor="vehicleYear">Year</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["vehicleYear"] != undefined
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <select
                                                        id="vehicleYear"
                                                        onChange={(e) => {
                                                            if (position.details?.vehicle?.make !== "") {
                                                                searchVehicleModels(e.target.value, position.details?.vehicle?.make);
                                                                setVehicle(position.displayId, e.target.value, position.details?.vehicle?.make, "");
                                                            } else {
                                                                setVehicle(position.displayId, e.target.value, position.details?.vehicle?.make, position.details?.vehicle?.model);
                                                            }
                                                        }}
                                                        value={position.details?.vehicle?.year}
                                                        className={clsx(
                                                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["vehicleYear"] != undefined
                                                                ? "error"
                                                                : "",
                                                            "mt-2"
                                                        )}
                                                        style={{ alignItems: 'center', maxWidth: 160 }}
                                                    >
                                                        <option hidden value="">
                                                            Choose...
                                                        </option>
                                                        {modelYears.map((year) => (
                                                            <option key={year} value={year}>{year}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["vehicleYear"] != undefined && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {assetErrors[position.displayId - 1]["vehicleYear"]}
                                                    </span>
                                                )}
                                            </div>
                                            <div>
                                                <label htmlFor="vehicleMake">Make</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <SearchableDropdown
                                                            options={(position?.assetType === "motorcycle") ? motorcycleMakes :
                                                                position?.assetType === "boat" ? boatMakes :
                                                                    position?.assetType === "atv" ? atvMakes : vehicleMakes}
                                                            label="name"
                                                            id={"vehicleMake" + position.displayId}
                                                            selectedVal={position?.details?.vehicle?.make}
                                                            handleChange={(val: any) => {
                                                                if (position.details?.vehicle?.year !== "") {
                                                                    searchVehicleModels(position.details?.vehicle?.year, val);
                                                                    setVehicle(position.displayId, position.details?.vehicle?.year, val, "");
                                                                } else {
                                                                    setVehicle(position.displayId, position.details?.vehicle?.year, val, position.details?.vehicle?.model);
                                                                }
                                                            }}
                                                            customStyle={{ paddingRight: 40 }}
                                                            disableForm={true}
                                                            fetching={false}
                                                            displayError={assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["vehicleMake"] != undefined}
                                                        />
                                                    </span>
                                                </div>
                                                <small
                                                    className={clsx(
                                                        assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["vehicleMake"] != undefined ? "hidden" : "",
                                                        "text-gray-400"
                                                    )}
                                                >
                                                    <b>Example</b>: Honda, Audi, Toyota
                                                </small>
                                                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["vehicleMake"] != undefined && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {assetErrors[position.displayId - 1]["vehicleMake"]}
                                                    </span>
                                                )}
                                            </div>
                                            <div className={clsx(style.threeColFirst, "ml-4")}>
                                                <label htmlFor="vehicleYear">Model {(position?.assetType === "boat" || position?.assetType === "atv"
                                                    || position?.assetType === "other") ? "(Optional)" : ""}</label>
                                                {(position?.assetType === "" || position?.assetType === "car"
                                                    || position?.assetType === "truck" || position?.assetType === "motorcycle") ?
                                                    <>
                                                        <div className="flex relative">
                                                            <span>
                                                                <SearchableDropdown
                                                                    options={vehicleModels}
                                                                    label="name"
                                                                    id={"vehicleModel" + position.displayId}
                                                                    selectedVal={position?.details?.vehicle?.model}
                                                                    handleChange={(val: any) => {
                                                                        setVehicle(position.displayId, position.details?.vehicle?.year, position.details?.vehicle?.make, val);
                                                                    }}
                                                                    customStyle={{ paddingRight: 40 }}
                                                                    disableForm={true}
                                                                    fetching={fetchingModels}
                                                                    displayError={assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["vehicleModel"] != undefined}
                                                                />
                                                            </span>
                                                        </div>
                                                        <small
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["vehicleModel"] != undefined ? "hidden" : "",
                                                                "text-gray-400"
                                                            )}
                                                        >
                                                            <b>Example</b>: Civic, A5, Camry
                                                        </small>
                                                    </>
                                                    :
                                                    <>
                                                        <div className="flex relative">
                                                            <span>
                                                                <img
                                                                    src="assets/error-icon.svg"
                                                                    className={clsx(
                                                                        assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["vehicleModel"] != undefined
                                                                            ? ""
                                                                            : "hidden",
                                                                        "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                    )}
                                                                    alt="error"
                                                                    height={5}
                                                                    width={5}
                                                                />
                                                            </span>
                                                            <input
                                                                className={clsx(
                                                                    assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["vehicleModel"] != undefined
                                                                        ? "error"
                                                                        : "",
                                                                    "mt-2 w:[220px] md:w-[305px]"
                                                                )}
                                                                type="text"
                                                                id={"vehicleModel" + position.displayId}
                                                                onChange={(e) => {
                                                                    setVehicle(position.displayId, position.details?.vehicle?.year, position.details?.vehicle?.make, e.target.value);
                                                                }}
                                                                value={position.details?.vehicle?.model}
                                                                maxLength={30}
                                                            />
                                                        </div>
                                                    </>
                                                }
                                                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["vehicleModel"] != undefined && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {assetErrors[position.displayId - 1]["vehicleModel"]}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex md:flex-row flex-col">
                                            {assetValue(position, true, 2)}
                                            <div className="mt-5">
                                                <label htmlFor="ownershipType">Ownership Type {position?.assetType != "car"
                                                    && position?.assetType != "truck" && position?.assetType != "" ? "(Optional)" : ""}</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["ownershipType"] != undefined
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <select
                                                        id="ownershipType"
                                                        onChange={(e) => {
                                                            setVehicleOwnership(position.displayId, e.target.value)
                                                        }}
                                                        value={position.details?.vehicle?.ownership}
                                                        className={clsx(
                                                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["ownershipType"] != undefined
                                                                ? "error"
                                                                : "",
                                                            "mt-2 w:[220px] md:w-[305px]"
                                                        )}
                                                        style={{ alignItems: 'center' }}
                                                    >
                                                        <option hidden value="">
                                                            Choose...
                                                        </option>
                                                        <option value="own">Own</option>
                                                        <option value="lease">Lease</option>
                                                    </select>
                                                </div>
                                                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["ownershipType"] != undefined && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {assetErrors[position.displayId - 1]["ownershipType"]}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex md:flex-row flex-col">
                                            {assetOwnership(position, true)}
                                            {assetUtilization(position, false)}
                                        </div>
                                    </>
                                }

                                {position?.assetCategory === "realestate" &&
                                    <>
                                        <div className="col-span-1 mt-5">
                                            <div className={""}>
                                                <label htmlFor="search">Home address</label>
                                                {!position.details?.realEstate?.isCurrentResidence &&
                                                    <>
                                                        <div className="flex md:flex-row flex-col justify-between">
                                                            <div className="flex relative">
                                                                <span>
                                                                    <img
                                                                        src="assets/magnifying-glass-icon.svg"
                                                                        className={clsx(
                                                                            (assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["autoAddress"] != undefined)
                                                                                ? "hidden"
                                                                                : "",
                                                                            "w-5 h-5 absolute top-5 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                                        )}
                                                                        alt="search"
                                                                        height={5}
                                                                        width={5}
                                                                    />
                                                                    <img
                                                                        src="assets/error-icon.svg"
                                                                        className={clsx(
                                                                            (assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["autoAddress"] != undefined)
                                                                                ? ""
                                                                                : "hidden",
                                                                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                                        )}
                                                                        alt="error"
                                                                        height={5}
                                                                        width={5}
                                                                    />
                                                                </span>
                                                                <input
                                                                    autoComplete="nope"
                                                                    className={clsx(
                                                                        (assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["autoAddress"] != undefined)
                                                                            ? "error"
                                                                            : "",
                                                                        "mt-2"
                                                                    )}
                                                                    id={"realEstateAddressSearch" + position.displayId}
                                                                    type="text"
                                                                    placeholder="Start typing address"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="flex relative mt-2">
                                                            <p className="active underline cursor-pointer" onClick={() => toggleManualAddress(position.displayId)}>Enter address manually</p>
                                                        </div>
                                                    </>
                                                }

                                                {addressSelectionError && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        You must select an address from the list of options
                                                    </span>
                                                )}
                                                <div className={clsx(position.details?.realEstate?.address?.city != "" ? "" : "hidden")}>
                                                    <p id={"formatted-address" + position.displayId}></p>
                                                    {position.details?.realEstate?.address?.streetNumber != "" &&
                                                        <><span>
                                                            {position.details?.realEstate?.address?.unitType != "" && position.details?.realEstate?.address?.unitNumber != "" ? position.details?.realEstate?.address?.unitType + " " + position.details?.realEstate?.address?.unitNumber + "-" : ""}
                                                            {position.details?.realEstate?.address?.streetNumber} {position.details?.realEstate?.address?.streetName} {position.details?.realEstate?.address?.streetType}
                                                            {position.details?.realEstate?.address?.streetDirection != "" && " " + position.details?.realEstate?.address?.streetDirection}</span><br />
                                                        </>}
                                                    {position.details?.realEstate?.address?.city != "" && <><span >{position.details?.realEstate?.address?.city}</span> &nbsp;</>}
                                                    {position.details?.realEstate?.address?.province != "" && <><span >{position.details?.realEstate?.address?.province}</span>&nbsp;</>}
                                                    {position.details?.realEstate?.address?.postalCode != "" && <><span >{position.details?.realEstate?.address?.postalCode}</span><br /></>}
                                                    {position.details?.realEstate?.address?.country != "" && <span >{position.details?.realEstate?.address?.country}</span>}
                                                </div>
                                                <div
                                                    className={clsx(
                                                        position.details?.realEstate?.manualAddressEntered ? "" : "hidden",
                                                        "flex relative"
                                                    )}
                                                >
                                                    <div className="mt-5">
                                                        <ManualAddress passAddressData={getAddressData} addressData={position.details?.realEstate?.address}
                                                            addressErrors={assetAddrErrors} errorIndex={position.displayId - 1} isRealEstateAsset={true} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex md:flex-row flex-col mt-5">
                                            <div className={style.threeColFirst}>
                                                <label htmlFor="realEstateLot">Lot (optional)</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["realEstateLot"] != undefined
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <input
                                                        className={clsx(
                                                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["realEstateLot"] != undefined
                                                                ? "error"
                                                                : "",
                                                            "mt-2 w:[220px] md:w-[305px]"
                                                        )}
                                                        type="text"
                                                        id={position.displayId + "realEstateLot"}
                                                        onChange={(e) => {
                                                            setRealEstateLot(position.displayId, e.target.value)
                                                        }}
                                                        value={position.assetCategory == "realestate" ? position.details?.realEstate?.lot : ""}
                                                        style={{ maxWidth: 160 }}
                                                        maxLength={25}
                                                    />
                                                </div>
                                            </div>
                                            <div className={style.threeColFirst}>
                                                <label htmlFor="realEstateBlock">Block (optional)</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["realEstateBlock"] != undefined
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <input
                                                        className={clsx(
                                                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["realEstateBlock"] != undefined
                                                                ? "error"
                                                                : "",
                                                            "mt-2 w:[220px] md:w-[305px]"
                                                        )}
                                                        type="text"
                                                        id={position.displayId + "realEstateBlock"}
                                                        onChange={(e) => {
                                                            setRealEstateBlock(position.displayId, e.target.value)
                                                        }}
                                                        value={position.assetCategory == "realestate" ? position.details?.realEstate?.block : ""}
                                                        style={{ maxWidth: 160 }}
                                                        maxLength={25}
                                                    />
                                                </div>
                                            </div>
                                            <div className={style.threeColFirst}>
                                                <label htmlFor="realEstatePlan">Plan (optional)</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["realEstatePlan"] != undefined
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <input
                                                        className={clsx(
                                                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["realEstatePlan"] != undefined
                                                                ? "error"
                                                                : "",
                                                            "mt-2 w:[220px] md:w-[305px]"
                                                        )}
                                                        type="text"
                                                        id={position.displayId + "realEstatePlan"}
                                                        onChange={(e) => {
                                                            setRealEstatePlan(position.displayId, e.target.value)
                                                        }}
                                                        value={position.assetCategory == "realestate" ? position.details?.realEstate?.plan : ""}
                                                        style={{ maxWidth: 160 }}
                                                        maxLength={13}
                                                    />
                                                </div>
                                            </div>
                                            <div className={style.threeColFirst}>
                                                <label htmlFor="realEstateSection">Section (optional)</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["realEstateSection"] != undefined
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <input
                                                        className={clsx(
                                                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["realEstateSection"] != undefined
                                                                ? "error"
                                                                : "",
                                                            "mt-2 w:[220px] md:w-[305px]"
                                                        )}
                                                        type="text"
                                                        id={position.displayId + "realEstateSection"}
                                                        onChange={(e) => {
                                                            setRealEstateSection(position.displayId, e.target.value)
                                                        }}
                                                        value={position.assetCategory == "realestate" ? position.details?.realEstate?.section : ""}
                                                        style={{ maxWidth: 160 }}
                                                        maxLength={16}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex md:flex-row flex-col">
                                            <div className={clsx(style.twoColFirst, "mt-5")}>
                                                <label htmlFor="useOfProperty">Use of Property</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["useOfProperty"] != undefined
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <select
                                                        id="useOfProperty"
                                                        onChange={(e) => {
                                                            setPurposeOfProperty(position.displayId, e.target.value)
                                                        }}
                                                        value={position.assetCategory == "realestate" ? position.details?.realEstate?.occupancyType : ""}
                                                        className={clsx(
                                                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["useOfProperty"] != undefined
                                                                ? "error"
                                                                : "",
                                                            "mt-2 w:[220px] md:w-[305px]"
                                                        )}
                                                        style={{ alignItems: 'center' }}
                                                    >
                                                        <option hidden value="">
                                                            Choose...
                                                        </option>
                                                        <option value="primary">Primary Residence</option>
                                                        <option value="rental">Rental/Investment</option>
                                                        <option value="vacationhome">Vacation Home</option>
                                                        <option value="secondary">Secondary Property</option>
                                                        <option value="vacantland">Vacant Land</option>
                                                    </select>
                                                </div>
                                                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["useOfProperty"] != undefined && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {assetErrors[position.displayId - 1]["useOfProperty"]}
                                                    </span>
                                                )}
                                            </div>
                                            {position?.details?.realEstate?.occupancyType !== "vacantland" &&
                                                <div className={clsx(style.twoColFirst, "mt-5")}>
                                                    <label htmlFor="propertyType">Property Type</label>
                                                    <div className="flex relative">
                                                        <span>
                                                            <img
                                                                src="assets/error-icon.svg"
                                                                className={clsx(
                                                                    assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["propertyType"] != undefined
                                                                        ? ""
                                                                        : "hidden",
                                                                    "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                )}
                                                                alt="error"
                                                                height={5}
                                                                width={5}
                                                            />
                                                        </span>
                                                        <select
                                                            id="propertyType"
                                                            onChange={(e) => {
                                                                setPropertyType(position.displayId, e.target.value)
                                                            }}
                                                            value={position.assetCategory == "realestate" ? position.details?.realEstate?.propertyType : ""}
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["propertyType"] != undefined
                                                                    ? "error"
                                                                    : "",
                                                                "mt-2 w:[220px] md:w-[305px]"
                                                            )}
                                                            style={{ alignItems: 'center' }}
                                                        >
                                                            <option hidden value="">
                                                                Choose...
                                                            </option>
                                                            <option value="singlefamily">Single Family</option>
                                                            <option value="semidetached">Semi-Detached</option>
                                                            <option value="duplex">Duplex</option>
                                                            <option value="triplex">Triplex</option>
                                                            <option value="fourplex">Fourplex</option>
                                                            <option value="commercialmix">Commercial Mix</option>
                                                            <option value="condo">Condo</option>
                                                            <option value="townhouse">Townhouse</option>
                                                            <option value="vacationpropertytypea">Vacation Property-Type A</option>
                                                            <option value="vacationpropertytypeb">Vacation Property-Type B</option>
                                                        </select>
                                                    </div>
                                                    {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["propertyType"] != undefined && (
                                                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                            {assetErrors[position.displayId - 1]["propertyType"]}
                                                        </span>
                                                    )}
                                                </div>
                                            }
                                        </div>
                                        {position?.details?.realEstate?.occupancyType === "rental" && (
                                            <div className={clsx(style.twoColFirst, "mt-5")}>
                                                <label htmlFor={"ownershipPercentage" + position.displayId}>Ownership Percentage</label>
                                                <div className="flex relative mb-3">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["ownershipPercentage"] != undefined
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <DebouncedTextInput
                                                        className={clsx(
                                                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["ownershipPercentage"] != undefined
                                                                ? "error"
                                                                : "",
                                                            "mt-2 w:[220px] md:w-[305px]"
                                                        )}
                                                        type="number"
                                                        id={"ownershipPercentage" + position.displayId}
                                                        value={position.details?.realEstate?.ownershipPercentage}
                                                        maxLength={3}
                                                        onFocus={(e) => e.target.select()}
                                                        onCommit={(val) => setOwnershipPercentage(position.displayId, val)}
                                                    />
                                                </div>
                                                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["ownershipPercentage"] != undefined && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {assetErrors[position.displayId - 1]["ownershipPercentage"]}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <div className="flex md:flex-row flex-col">
                                            <div className={clsx(style.twoColFirst, "mt-5")}>
                                                <label htmlFor="originalPurchaseDate">Original Purchase Date (optional)</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["originalPurchaseDate"] != undefined
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[195px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <div>
                                                        <DatePicker
                                                            className={style.datePickerInput}
                                                            format="DD MMM YYYY"
                                                            maxDate={dayjs()}
                                                            value={position.assetCategory === 'realestate' ? dayjs(position.details?.realEstate?.originalPurchaseDate, 'DD/MM/YYYY') : null}
                                                            onChange={(e) => {
                                                                if (e != undefined) {
                                                                    setOriginalPurchaseDate(position.displayId, e.format('DD/MM/YYYY'));
                                                                }
                                                            }}
                                                            slotProps={{
                                                                textField: {
                                                                    InputProps: {
                                                                        sx: {
                                                                            borderRadius: '12px',
                                                                            backgroundColor: 'white',
                                                                            padding: "0px",
                                                                            alignItems: "center",
                                                                            height: "44px",
                                                                            marginTop: "8px",
                                                                            width: window.innerWidth < 768 ? 350 : 305
                                                                        },
                                                                    },
                                                                    error: assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["originalPurchaseDate"] != undefined,
                                                                },
                                                                inputAdornment: {
                                                                    sx: {
                                                                        height: "44px",
                                                                    }
                                                                }
                                                            }}
                                                            localeText={{
                                                                fieldMonthPlaceholder: (params) => (params.contentType === 'letter' ? 'MMM' : 'MM'),
                                                            }}
                                                        />
                                                    </div>

                                                </div>
                                                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["originalPurchaseDate"] != undefined && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {assetErrors[position.displayId - 1]["originalPurchaseDate"]}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-5">
                                                <label htmlFor={position.displayId + "originalPrice"}>Original Purchase Price (optional)</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["originalPrice"] != undefined
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <CurrencyInput
                                                        id={position.displayId + "originalPrice"}
                                                        placeholder="$"
                                                        prefix={"$"}
                                                        decimalsLimit={2}
                                                        decimalScale={2}
                                                        allowNegativeValue={false}
                                                        maxLength={9}
                                                        value={position.assetCategory == "realestate" ? position.details?.realEstate?.originalPurchasePriceText : ""}
                                                        className={clsx(
                                                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["originalPrice"] != undefined
                                                                ? "error"
                                                                : "",
                                                            "mt-2 w:[220px] md:w-[305px]"
                                                        )}
                                                        onValueChange={(value, name, values) => value != undefined ? setOriginalPurchasePrice(position.displayId, value) : setOriginalPurchasePrice(position.displayId, "")}
                                                    />
                                                </div>
                                                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["originalPrice"] != undefined && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {assetErrors[position.displayId - 1]["originalPrice"]}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {position?.details?.realEstate?.occupancyType === "rental" && (
                                            <div className="flex md:flex-row flex-col">
                                                {rentalMonthlyIncome(position, true)}
                                                {rentalUnitCount(position, false)}
                                            </div>
                                        )}

                                        {position?.details?.realEstate?.occupancyType !== "vacantland" && position?.details?.realEstate?.occupancyType !== "rental" && (
                                            <div className="flex md:flex-row flex-col">
                                                {rentalUnitCount(position, true)}
                                            </div>
                                        )}



                                        {position.details?.realEstate?.occupancyType === "primary" &&
                                            <>
                                                <div className="flex md:flex-row flex-col">
                                                    <div className={clsx("mt-5", style.twoColFirst)}>
                                                        <label>Is there a rental component to the property?</label>
                                                        <div className="grid grid-flow-row sm:grid-flow-col">
                                                            <ul className="grid grid-cols-3 gap-x-2 mt-2 max-w-md" style={{ maxWidth: "20rem" }}>
                                                                <li className="relative">
                                                                    <input
                                                                        type="radio"
                                                                        value="yes"
                                                                        id={position.displayId + "yes-rental-component"}
                                                                        checked={position.details?.realEstate?.rentalComponent === "yes"}
                                                                        onChange={(e) => {
                                                                            setRentalComponent(position.displayId, e.target.value)
                                                                        }}
                                                                        className="sr-only peer "
                                                                    />
                                                                    <label
                                                                        className={clsx(
                                                                            style.phoneLabel,
                                                                            "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["rentalComponent"] != undefined
                                                                                ? style.labelError
                                                                                : ""
                                                                        )}
                                                                        htmlFor={position.displayId + "yes-rental-component"}
                                                                    >
                                                                        <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>Yes</p>
                                                                    </label>
                                                                </li>
                                                                <li className="relative">
                                                                    <input
                                                                        type="radio"
                                                                        value="no"
                                                                        id={position.displayId + "no-rental-component"}
                                                                        checked={position.details?.realEstate?.rentalComponent === "no"}
                                                                        onChange={(e) => {
                                                                            setRentalComponent(position.displayId, e.target.value)
                                                                        }}
                                                                        className="sr-only peer leading-6"
                                                                    />
                                                                    <label
                                                                        className={clsx(
                                                                            style.phoneLabel,
                                                                            "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["rentalComponent"] != undefined
                                                                                ? style.labelError
                                                                                : ""
                                                                        )}
                                                                        htmlFor={position.displayId + "no-rental-component"}
                                                                    >
                                                                        <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>No</p>
                                                                    </label>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                        {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["rentalComponent"] != undefined && (
                                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                {assetErrors[position.displayId - 1]["rentalComponent"]}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {position?.details?.realEstate?.rentalComponent === "yes" &&
                                                        rentalMonthlyIncome(position, true)
                                                    }
                                                </div>
                                            </>
                                        }

                                        <div className="flex md:flex-row flex-col mt-5">
                                            <div className={clsx(style.twoColFirst)}>
                                                <label htmlFor="title">Property Taxes</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["propertyTax"] != undefined
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <CurrencyInput
                                                        id={position.displayId + "propertyTax"}
                                                        placeholder="/per year"
                                                        prefix={"$"}
                                                        decimalsLimit={2}
                                                        decimalScale={2}
                                                        allowNegativeValue={false}
                                                        maxLength={7}
                                                        value={position.assetCategory == "realestate" ? position.details?.realEstate?.propertyTaxText : ""}
                                                        className={clsx(
                                                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["propertyTax"] != undefined
                                                                ? "error"
                                                                : "",
                                                            "mt-2 w:[220px] md:w-[305px]"
                                                        )}
                                                        onValueChange={(value, name, values) => value != undefined ? setPropertyTax(position.displayId, value) : setPropertyTax(position.displayId, "")}
                                                    />
                                                </div>
                                                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["propertyTax"] != undefined && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {assetErrors[position.displayId - 1]["propertyTax"]}
                                                    </span>
                                                )}
                                            </div>
                                            <div>
                                                <label htmlFor="title">Condo/Maintenance Fee (if Applicable)</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["maintenanceFee"] != undefined
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <CurrencyInput
                                                        id={position.displayId + "maintenanceFee"}
                                                        placeholder="/per month"
                                                        prefix={"$"}
                                                        decimalsLimit={2}
                                                        decimalScale={2}
                                                        allowNegativeValue={false}
                                                        maxLength={7}
                                                        value={position.assetCategory == "realestate" ? position.details?.realEstate?.maintenanceFeeText : ""}
                                                        className={clsx(
                                                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["maintenanceFee"] != undefined
                                                                ? "error"
                                                                : "",
                                                            "mt-2 w:[220px] md:w-[305px]"
                                                        )}
                                                        onValueChange={(value, name, values) => value != undefined ? setMaintenanceCost(position.displayId, value) : setMaintenanceCost(position.displayId, "")}
                                                    />
                                                </div>
                                                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["maintenanceFee"] != undefined && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {assetErrors[position.displayId - 1]["maintenanceFee"]}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex md:flex-row flex-col mt-5">
                                            <div>
                                                <label htmlFor="title">Heat/Utility Fees</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["heatingCost"] != undefined
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <CurrencyInput
                                                        id={position.displayId + "heatingCost"}
                                                        placeholder="/per month"
                                                        prefix={"$"}
                                                        decimalsLimit={2}
                                                        decimalScale={2}
                                                        allowNegativeValue={false}
                                                        maxLength={7}
                                                        value={position.assetCategory == "realestate" ? position.details?.realEstate?.heatingCostText : ""}
                                                        className={clsx(
                                                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["heatingCost"] != undefined
                                                                ? "error"
                                                                : "",
                                                            "mt-2 w:[220px] md:w-[305px]"
                                                        )}
                                                        onValueChange={(value, name, values) => value != undefined ? setHeatingCost(position.displayId, value) : setHeatingCost(position.displayId, "")}
                                                    />
                                                </div>
                                                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["heatingCost"] != undefined && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {assetErrors[position.displayId - 1]["heatingCost"]}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex md:flex-row flex-col">
                                            {assetOwnership(position, true)}
                                            {assetValue(position, false, 2)}
                                        </div>

                                        <hr className="mt-5" />

                                        <div className="flex md:flex-row flex-col">
                                            <div className={clsx(style.twoColFirst, "mt-5")}>
                                                <label htmlFor="futureUsage">Future Usage</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["futureUsage"] != undefined
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <select
                                                        id="futureUsage"
                                                        onChange={(e) => {
                                                            setRealEstateFutureUsage(position.displayId, e.target.value)
                                                        }}
                                                        value={position.assetCategory == "realestate" ? position.details?.realEstate?.futureUsage : ""}
                                                        className={clsx(
                                                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["futureUsage"] != undefined
                                                                ? "error"
                                                                : "",
                                                            "mt-2 w:[220px] md:w-[305px]"
                                                        )}
                                                        style={{ alignItems: 'center' }}
                                                    >
                                                        <option hidden value="">
                                                            Choose...
                                                        </option>
                                                        <option value="primary">Will remain as my Primary</option>
                                                        <option value="selling">Will be selling it</option>
                                                        <option value="renting">Will be renting it out</option>
                                                        <option value="vacationproperty">Will become secondary/vacation property</option>
                                                        <option value="endinglease">Will be ending my lease</option>
                                                    </select>
                                                </div>
                                                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["futureUsage"] != undefined && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {assetErrors[position.displayId - 1]["futureUsage"]}
                                                    </span>
                                                )}
                                            </div>
                                            {assetUtilization(position, false)}
                                        </div>

                                        {position.details?.realEstate?.futureUsage === "selling" &&
                                            <div className="flex md:flex-row flex-col">
                                                <div className={clsx("mt-5", style.twoColFirst)}>
                                                    <label>Has the property been sold?</label>
                                                    <div className="grid grid-flow-row sm:grid-flow-col">
                                                        <ul className="grid grid-cols-3 gap-x-2 mt-2 max-w-md" style={{ maxWidth: "20rem" }}>
                                                            <li className="relative">
                                                                <input
                                                                    type="radio"
                                                                    value="yes"
                                                                    id={position.displayId + "yes-property-sold"}
                                                                    checked={position.details?.realEstate?.hasPropertyBeenSold === "yes"}
                                                                    onChange={(e) => {
                                                                        setPropertySold(position.displayId, e.target.value)
                                                                    }}
                                                                    className="sr-only peer "
                                                                />
                                                                <label
                                                                    className={clsx(
                                                                        style.phoneLabel,
                                                                        "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                                        assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["propertySold"] != undefined
                                                                            ? style.labelError
                                                                            : ""
                                                                    )}
                                                                    htmlFor={position.displayId + "yes-property-sold"}
                                                                >
                                                                    <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>Yes</p>
                                                                </label>
                                                            </li>
                                                            <li className="relative">
                                                                <input
                                                                    type="radio"
                                                                    value="no"
                                                                    id={position.displayId + "no-property-sold"}
                                                                    checked={position.details?.realEstate?.hasPropertyBeenSold === "no"}
                                                                    onChange={(e) => {
                                                                        setPropertySold(position.displayId, e.target.value)
                                                                    }}
                                                                    className="sr-only peer leading-6"
                                                                />
                                                                <label
                                                                    className={clsx(
                                                                        style.phoneLabel,
                                                                        "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                                        assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["propertySold"] != undefined
                                                                            ? style.labelError
                                                                            : ""
                                                                    )}
                                                                    htmlFor={position.displayId + "no-property-sold"}
                                                                >
                                                                    <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>No</p>
                                                                </label>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                    {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["propertySold"] != undefined && (
                                                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                            {assetErrors[position.displayId - 1]["propertySold"]}
                                                        </span>
                                                    )}
                                                </div>
                                                {position?.details?.realEstate?.hasPropertyBeenSold === "yes" &&
                                                    <div className="mt-5">
                                                        <label htmlFor="soldDate">Closing Date</label>
                                                        <div className="flex relative">
                                                            <span>
                                                                <img
                                                                    src="assets/error-icon.svg"
                                                                    className={clsx(
                                                                        assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["soldDate"] != undefined
                                                                            ? ""
                                                                            : "hidden",
                                                                        "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[195px]"
                                                                    )}
                                                                    alt="error"
                                                                    height={5}
                                                                    width={5}
                                                                />
                                                            </span>
                                                            <div>
                                                                <DatePicker
                                                                    className={style.datePickerInput}
                                                                    format="DD MMM YYYY"
                                                                    minDate={dayjs()}
                                                                    value={position.assetCategory === 'realestate' ? dayjs(position.details?.realEstate?.soldDate, 'DD/MM/YYYY') : null}
                                                                    onChange={(e) => {
                                                                        if (e != undefined) {
                                                                            setPropertySoldDate(position.displayId, e.format('DD/MM/YYYY'));
                                                                        }
                                                                    }}
                                                                    slotProps={{
                                                                        textField: {
                                                                            InputProps: {
                                                                                sx: {
                                                                                    borderRadius: '12px',
                                                                                    backgroundColor: 'white',
                                                                                    padding: "0px",
                                                                                    alignItems: "center",
                                                                                    height: "44px",
                                                                                    marginTop: "8px",
                                                                                    width: window.innerWidth < 768 ? 350 : 305
                                                                                },
                                                                            },
                                                                            error: assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["soldDate"] != undefined,
                                                                        },
                                                                        inputAdornment: {
                                                                            sx: {
                                                                                height: "44px",
                                                                            }
                                                                        }
                                                                    }}
                                                                    localeText={{
                                                                        fieldMonthPlaceholder: (params) => (params.contentType === 'letter' ? 'MMM' : 'MM'),
                                                                    }}
                                                                />
                                                            </div>

                                                        </div>
                                                        {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["soldDate"] != undefined && (
                                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                {assetErrors[position.displayId - 1]["soldDate"]}
                                                            </span>
                                                        )}
                                                    </div>
                                                }
                                            </div>
                                        }
                                    </>
                                }

                                {position?.assetCategory === "lifeinsurance" &&
                                    <>
                                        <div className="flex md:flex-row flex-col">
                                            {assetDescription(position, true, false)}
                                            {assetValue(position, false, 1)}
                                        </div>
                                        <div className="flex md:flex-row flex-col">
                                            <div className={clsx(style.twoColFirst, "mt-5")}>
                                                <label htmlFor={position.displayId + "cashSurrenderValue"}>Cash Surrender Value</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["cashSurrenderValue"] != undefined
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <CurrencyInput
                                                        id={position.displayId + "cashSurrenderValue"}
                                                        placeholder="$"
                                                        prefix={"$"}
                                                        decimalsLimit={2}
                                                        decimalScale={2}
                                                        allowNegativeValue={false}
                                                        maxLength={9}
                                                        value={position.assetCategory == "lifeinsurance" ? position.details?.lifeInsurance?.cashSurrenderValueText : ""}
                                                        className={clsx(
                                                            assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["cashSurrenderValue"] != undefined
                                                                ? "error"
                                                                : "",
                                                            "mt-2 w:[220px] md:w-[305px]"
                                                        )}
                                                        onValueChange={(value, name, values) => value != undefined ? setCashSurrenderValue(position.displayId, value) : setCashSurrenderValue(position.displayId, "")}
                                                    />
                                                </div>
                                                {assetErrors[position.displayId - 1] != undefined && assetErrors[position.displayId - 1]["cashSurrenderValue"] != undefined && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {assetErrors[position.displayId - 1]["cashSurrenderValue"]}
                                                    </span>
                                                )}
                                            </div>
                                            {assetOwnership(position, true)}
                                        </div>
                                        <div className="flex md:flex-row flex-col">
                                            {assetUtilization(position, false)}
                                        </div>
                                    </>
                                }

                                {(position?.assetCategory === "gift" || position?.assetCategory === "other" || position?.assetCategory === "depositonpurchase"
                                    || position?.assetCategory === "builderdeposit") &&
                                    <>
                                        <div className="flex md:flex-row flex-col">
                                            {assetValue(position, true, 3)}
                                            {assetDescription(position, false, position?.assetCategory !== "other")}
                                        </div>
                                        <div className="flex md:flex-row flex-col">
                                            {assetOwnership(position, true)}
                                            {position?.assetCategory === "other" && assetUtilization(position, false)}
                                        </div>
                                    </>
                                }

                                <hr className="mt-5" />
                            </>
                        }
                    </motion.div>
                ))
                }
            </AnimatePresence>
        )
    };

    const goBack = async () => {
        if (editMode) {
            //If the applicant decides to go back to summary after declaring ownership of their current address, revert back to "rent" for status
            if (addingOwnRealEstate) {
                setIsSaving(true);
                dispatch(setResidentStatus(sessionData.cachedApplicant?.residentStatus != undefined ? sessionData.cachedApplicant.residentStatus : "rent"));
                await client.apiApplicantsAddressPut(homeaddress.id, applicant.id, new AddressDto({
                    id: homeaddress.id,
                    streetNumber: homeaddress.streetNumber,
                    streetName: homeaddress.streetName,
                    streetType: homeaddress.streetType,
                    streetDirection: homeaddress.streetDirection,
                    unitType: homeaddress.unitType,
                    unitNumber: homeaddress.unitNumber,
                    city: homeaddress.city,
                    region: homeaddress.province,
                    postalCode: homeaddress.postalCode,
                    country: homeaddress.country,
                    timeAtResidenceInMonths: (+applicant.residenceYears * 12) + +applicant.residenceMonths,
                    residenceStatusType_Name: "rent",
                    deliveryMode: undefined,
                    modeNumber: undefined,
                    deliveryMode2: undefined,
                    modeNumber2: undefined,
                    isMailing: false
                })).then((result) => {
                    console.log("Trellis Address Updated");
                    setIsSaving(false);
                }).catch((error) => {
                    console.log("Error updating trellis address");
                    setIsSaving(false);
                });
            }
            setFetchingCachedData(true);
            dispatch(clearAssetState());
            sessionData.cachedAssets.forEach((asset) => {
                dispatch(addAsset(asset));
            });
        }
        navigate(-1);
    }

    return (
        <>
            <SlideTransition>
                <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
                    <div
                        className={style.addHeading}
                        role="region"
                        aria-label="Address Header"
                    >
                        <div className="max-md:ml-2">
                            <h1 aria-label="Address Header">Tell us about where you will get your down payment</h1>
                            <p className="sub-heading">Select at least one asset or additional down payment source to continue. You can update this later when our associates get in touch with you to process the application.</p>
                        </div>
                        <div className={"flex flex-row mt-5"} style={{ maxWidth: "85%" }}>
                            <FormProvider {...methods}>
                                <form
                                    autoComplete="off"
                                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                                    style={{ width: '-webkit-fill-available', paddingRight: '10%' }}
                                >
                                    {displayError &&
                                        <div className={clsx("flex flex-row pcaautocomplete mt-5", style.errorContainer)}>
                                            <img src="assets/error-info-icon.svg" alt="error" />
                                            <span className="text-sm ml-2 mt-1">
                                                {errorMessage}
                                            </span>
                                        </div>
                                    }
                                    <ConfirmDialog />
                                    {assets != undefined && assets.length > 0 && !fetchingCachedData &&
                                        <>
                                            {assetsList()}
                                        </>
                                    }

                                    <div className="mb-5">
                                        <button
                                            type="button"
                                            onClick={addCurrAsset}
                                            className="secondary mt-5 w-full sm:w-28 h-11 px-6"
                                            style={{ width: '-webkit-fill-available', color: 'var(--brand-blue)' }}>
                                            <img
                                                className="mr-3"
                                                style={{ marginBottom: 2 }}
                                                src="assets/add-circle.svg"
                                                alt="tooltip"
                                                width={15}
                                                height={15}
                                            />Add {assets && assets.length > 0 && !fetchingCachedData ? "additional sources" : "a source"}
                                        </button>
                                    </div>

                                    <div className="flex max-sm:hidden">
                                        <div className="mb-5 mr-5">
                                            <button onClick={() => goBack()}
                                                type="button"
                                                className="back mt-5 w-169 sm:w-28 h-11 px-6">
                                                {editMode ? "Cancel" : "Back"}
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                style={{ background: assets && assets.length === 0 ? "#00558052" : "var(--brand-blue)" }}
                                                disabled={assets.length === 0 || isSaving}
                                            >
                                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                                {editMode ? "Save" : "Continue"}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="sm:hidden">
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                style={{ background: assets && assets.length === 0 ? "#00558052" : "var(--brand-blue)" }}
                                                disabled={assets.length === 0 || isSaving}
                                            >
                                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                                {editMode ? "Save" : "Continue"}
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button onClick={() => goBack()}
                                                type="button"
                                                className="back mt-5 w-full sm:w-28 h-11 px-6">
                                                {editMode ? "Cancel" : "Back"}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </SlideTransition>
        </>
    );
}